import { useState, useEffect, useRef, useCallback, Fragment } from 'react';
import ReactDOM, { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'motion/react';
import { MapPin, Edit2, Briefcase, Plus, X, ArrowLeft, MessageSquare, Crown, Lock, Clock, Heart, Share2, Eye, TrendingUp, Home, Camera, Trash2, Upload, AlertTriangle, CheckCircle, Sparkles, Image as ImageIcon, Lightbulb, Star, Target, LogOut, Settings, Ban, User, NotebookText } from 'lucide-react';
import { Loader } from './ui/Loader';
import { LinkifiedText } from './ui/LinkifiedText';
import { projectId, API_BASE } from '../utils/supabase/info';
import { CreatePostPage } from './CreatePostPage';
import { PostCard } from './PostCard';
import { PostDetailView } from './PostDetailView';
import { EmployerProfileView } from './EmployerProfileView';
import { CATEGORIES } from '../utils/categories-tags';
import { formatLastActive } from '../utils/date-utils';
import { NORWEGIAN_COUNTIES } from '../utils/norwegian-locations';
import { toast } from '../utils/toast';
import { containsProfanity } from '../utils/profanity-filter';
import { EditPostPage } from './EditPostPage';
import { ProjectCardWithActions } from './ProjectCardWithActions';
import { ConfirmDialog } from './ConfirmDialog';
import { handleApiError } from '../utils/api-error-handler';
import { devLog, devError } from '../utils/logger';
import { canCreateGig, canCreateTalentPackage, canShowGigInterest, canTalentStartConversation, canUploadVideo, canUseFavorites, hasEmployerMessagingAccess } from '../utils/accessPolicy';
import { useModalFocus } from '../utils/hooks/useModalFocus';
import { useDirtyFormGuard } from '../utils/hooks/useDirtyFormGuard';
import { useFormDraftPersistence } from '../utils/hooks/useFormDraftPersistence';
import { useBeforeUnloadDirty } from '../utils/hooks/useBeforeUnloadDirty';
import { useImageCropModal } from '../utils/hooks/useImageCropModal';
import { getCachedProfile, setCachedProfile } from '../utils/profileCache';
import { compressImageForUpload, PROFILE_SHOWCASE_PRESET } from '../utils/imageCompression';
import { AVATAR_IMAGE_SPEC, CONTENT_IMAGE_SPEC_4_3 } from '../utils/media/imageCrop';
import { PageShell } from './layout/PageShell';
import { TalentPackageCard } from './TalentPackageCard';
import { TalentPackageDetailModal } from './TalentPackageDetailModal';
import { ShowPackageRequestModal } from './ShowPackageRequestModal';
import { TalentPackageInterestsView } from './TalentPackageInterestsView';
import { CreateTalentPackageModal } from './CreateTalentPackageModal';
import { DesktopSubPageModal } from './DesktopSubPageModal';


const SETTINGS_SHEET_BODY_CLASS = 'talentfunn-settings-sheet-open';

interface UserProfileViewProps {
  userId: string;
  accessToken: string | null;
  /** Logged-in user id – used to fetch viewer profile when currentUserProfile is not yet loaded */
  currentUserId?: string;
  isOwnProfile: boolean;
  currentUserProfile?: any;
  /** When true (desktop), edit drawer is portaled to body so profile is visible behind */
  isDesktop?: boolean;
  onStartMessage?: (userId: string) => void;
  onLoginRequired?: () => void;
  onViewFavorites?: () => void;
  onViewSavedSearches?: () => void;
  onSignOut?: () => void;
  onNavigate?: (view: string) => void;
  onCreateGig?: () => void; // Callback to navigate to gigs page and open create gig modal
  onProfileUpdate?: () => void | Promise<void>;
}

export function UserProfileView({ userId, accessToken, currentUserId, isOwnProfile, currentUserProfile, isDesktop = false, onStartMessage, onLoginRequired, onViewFavorites, onViewSavedSearches, onSignOut, onNavigate, onCreateGig, onProfileUpdate }: UserProfileViewProps) {
  const [profile, setProfile] = useState<any>(null);
  const [posts, setPosts] = useState<any[]>([]);
  const [draftPosts, setDraftPosts] = useState<any[]>([]);
  const [packages, setPackages] = useState<any[]>([]);
  const [loadingPackages, setLoadingPackages] = useState(true);
  const [packageRequestIds, setPackageRequestIds] = useState<string[]>([]);
  const [selectedPackage, setSelectedPackage] = useState<any>(null);
  const [requestPackage, setRequestPackage] = useState<any>(null);
  const [editingPackage, setEditingPackage] = useState<any>(null);
  const [creatingPackage, setCreatingPackage] = useState(false);
  const [activePackageInterests, setActivePackageInterests] = useState<{ id: string; title: string } | null>(null);
  const [featuredPublicPostId, setFeaturedPublicPostId] = useState<string | null>(null);
  const [isCreatingPost, setIsCreatingPost] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingPosts, setLoadingPosts] = useState(true);
  const [isFavorited, setIsFavorited] = useState(false);
  const [togglingFavorite, setTogglingFavorite] = useState(false);
  const [profileStats, setProfileStats] = useState<any>(null);
  const [showShareModal, setShowShareModal] = useState(false);
  const [selectedPost, setSelectedPost] = useState<any>(null);
  const [editingPost, setEditingPost] = useState<any>(null);
  const [uploadingProfileImage, setUploadingProfileImage] = useState(false);
  const [showProfileImageOptions, setShowProfileImageOptions] = useState(false);
  const [showWelcomeSplash, setShowWelcomeSplash] = useState(false);
  const [uploadingShowcaseImage, setUploadingShowcaseImage] = useState(false);
  const [showSettingsSheet, setShowSettingsSheet] = useState(false); // New state for settings sheet
  const [showEditDrawer, setShowEditDrawer] = useState(false); // State for edit profile drawer
  const [showDiscardEditConfirm, setShowDiscardEditConfirm] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false); // State for delete confirmation
  const [postToDelete, setPostToDelete] = useState<string | null>(null); // Store post ID to delete
  const [deletingPost, setDeletingPost] = useState(false);
  const [showDeletePackageConfirm, setShowDeletePackageConfirm] = useState(false);
  const [packageToDelete, setPackageToDelete] = useState<string | null>(null);
  const [deletingPackage, setDeletingPackage] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false); // State for report profile modal
  const [reportReason, setReportReason] = useState(''); // Report reason
  const [reportDescription, setReportDescription] = useState(''); // Report description
  const { openCrop, cropModal } = useImageCropModal();
  const [showBlockedUsers, setShowBlockedUsers] = useState(false); // State for blocked users modal
  const [blockedUsers, setBlockedUsers] = useState<any[]>([]); // List of blocked users
  const [loadingBlockedUsers, setLoadingBlockedUsers] = useState(false); // Loading state for blocked users
  const [savingProfile, setSavingProfile] = useState(false);
  const [sendingReport, setSendingReport] = useState(false);
  const [unblockingUserId, setUnblockingUserId] = useState<string | null>(null);
  /** Fallback when App has not yet passed currentUserProfile – so Send melding / Favoritt show for employer_plus */
  const [viewerProfile, setViewerProfile] = useState<any>(null);

  // Keep global chrome visible behind small dialogs (share/report),
  // but hide it for full-screen drawers/overlays.
  useModalFocus(showShareModal || showReportModal);
  useModalFocus(showBlockedUsers || showWelcomeSplash || showEditDrawer, { fullscreen: true });

  const isIosPwa = typeof document !== 'undefined' && document.documentElement.classList.contains('ios-pwa');
  const tfMode = typeof document !== 'undefined' ? document.documentElement.getAttribute('data-tf-mode') : null;
  const isAndroidPwa = tfMode === 'android-pwa';
  const isAndroid = typeof document !== 'undefined' && typeof navigator !== 'undefined' && !document.documentElement.classList.contains('ios-pwa') && (document.documentElement.classList.contains('android-pwa') || /android/i.test(navigator.userAgent));
  const androidHeaderPadTop = isAndroidPwa ? '0.5rem' : '1rem';

  // Collapsible bio state
  const [bioExpanded, setBioExpanded] = useState(false);

  // Effective viewer profile: use App's currentUserProfile when available, else our fetched viewerProfile
  const effectiveViewerProfile = currentUserProfile ?? viewerProfile;

  // Ref to track if we've checked welcome splash for this user
  const hasCheckedWelcome = useRef(false);
  const hasProcessedInitialAuthRefresh = useRef(false);

  // Form fields
  const [name, setName] = useState('');
  const [bio, setBio] = useState('');
  const [location, setLocation] = useState('');
  const [county, setCounty] = useState('');
  const [skillInput, setSkillInput] = useState('');
  const [skills, setSkills] = useState<string[]>([]);
  const [categories, setCategories] = useState<string[]>([]);
  const [jobWishes, setJobWishes] = useState('');
  const {
    isDirty: isEditProfileDirty,
    markDirty: markEditProfileDirty,
    resetDirty: resetEditProfileDirty,
    setDirty: setEditProfileDirty,
    canApplyRemoteUpdate,
  } = useDirtyFormGuard(false);

  const handleRestoreEditProfileDraft = useCallback(
    (payload: {
      name: string;
      bio: string;
      location: string;
      county: string;
      skills: string[];
      categories: string[];
      jobWishes: string;
    }) => {
      setName(payload?.name || '');
      setBio(payload?.bio || '');
      setLocation(payload?.location || '');
      setCounty(payload?.county || '');
      setSkills(Array.isArray(payload?.skills) ? payload.skills : []);
      setCategories(Array.isArray(payload?.categories) ? payload.categories : []);
      setJobWishes(payload?.jobWishes || '');
      setEditProfileDirty(true);
    },
    [setEditProfileDirty]
  );

  const { clearDraft: clearEditProfileDraft } = useFormDraftPersistence({
    key: `talentfunn:draft:user-profile:${userId}`,
    enabled: isOwnProfile && showEditDrawer,
    value: { name, bio, location, county, skills, categories, jobWishes },
    onRestore: handleRestoreEditProfileDraft,
  });

  useBeforeUnloadDirty(showEditDrawer && isEditProfileDirty);
  const applyProfileToState = useCallback((nextProfile: any) => {
    setProfile(nextProfile);
    setName(nextProfile?.name || '');
    setBio(nextProfile?.bio || '');
    setLocation(nextProfile?.location || '');
    setCounty(nextProfile?.county || '');
    setSkills(nextProfile?.skills || []);
    setCategories(nextProfile?.categories || []);
    setJobWishes(nextProfile?.jobWishes || '');
  }, []);

  useEffect(() => {
    // Reset view state only when profile context changes (not on token rotation)
    const cachedProfile = getCachedProfile(userId);
    setLoading(!cachedProfile);
    setLoadingPosts(true);
    setLoadingPackages(true);
    setPosts([]);
    setDraftPosts([]);
    setPackages([]);
    setPackageRequestIds([]);
    setFeaturedPublicPostId(null);
    if (cachedProfile) {
      applyProfileToState(cachedProfile);
    } else {
      setProfile(null);
    }
    setSelectedPost(null);
    setSelectedPackage(null);
    setEditingPost(null);
    setEditingPackage(null);
    setIsFavorited(false);
    setProfileStats(null);
    hasCheckedWelcome.current = false; // Reset welcome check for new user

    fetchProfile();
    fetchUserPosts();
    fetchUserPackages();
    if (accessToken && !isOwnProfile) {
      checkFavoriteStatus();
      trackProfileView();
    }
    if (isOwnProfile && accessToken) {
      fetchProfileStats();
      fetchUserDrafts();
    }
  }, [userId, isOwnProfile, applyProfileToState]);

  useEffect(() => {
    // Keep auth-dependent data fresh when token changes, without clearing editor state.
    if (!hasProcessedInitialAuthRefresh.current) {
      hasProcessedInitialAuthRefresh.current = true;
      return;
    }
    if (!userId) return;
    fetchUserPosts();
    fetchUserPackages();
    if (accessToken && !isOwnProfile) {
      checkFavoriteStatus();
      trackProfileView();
    }
    if (isOwnProfile && accessToken) {
      fetchProfileStats();
      fetchUserDrafts();
    }
  }, [accessToken, userId, isOwnProfile]);

  // Keep bottom nav hidden while Kontoinnstillinger drawer is open
  useEffect(() => {
    if (typeof document === 'undefined') return;
    if (showSettingsSheet) {
      document.body.classList.add(SETTINGS_SHEET_BODY_CLASS);
    } else {
      document.body.classList.remove(SETTINGS_SHEET_BODY_CLASS);
    }
    return () => {
      document.body.classList.remove(SETTINGS_SHEET_BODY_CLASS);
    };
  }, [showSettingsSheet]);

  // Let browser/device back close Kontoinnstillinger drawer before leaving page.
  useEffect(() => {
    if (!showSettingsSheet) return;
    window.history.pushState({ modal: 'user-settings-sheet' }, '');
    const handlePopState = (e: PopStateEvent) => {
      if (e.state?.modal === 'user-settings-sheet') return;
      setShowSettingsSheet(false);
    };
    window.addEventListener('popstate', handlePopState);
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [showSettingsSheet]);

  // Keep own profile UI in sync without retriggering network fetches for every profile object update
  useEffect(() => {
    if (!isOwnProfile || !currentUserProfile) return;
    if (!canApplyRemoteUpdate(showEditDrawer)) return;
    const mappedProfile = {
      ...currentUserProfile,
      user_type: currentUserProfile.userType, // Map userType to user_type
    };
    applyProfileToState(mappedProfile);
    setCachedProfile(userId, mappedProfile);
    setLoading(false);
  }, [isOwnProfile, currentUserProfile, userId, applyProfileToState, showEditDrawer, canApplyRemoteUpdate]);

  // Re-apply server profile when closing edit drawer without save (so read-only view shows server state).
  const syncProfileFromServer = useCallback(() => {
    if (!currentUserProfile) return;
    const mappedProfile = {
      ...currentUserProfile,
      user_type: currentUserProfile.userType,
    };
    applyProfileToState(mappedProfile);
    setCachedProfile(userId, mappedProfile);
  }, [currentUserProfile, userId, applyProfileToState]);

  const requestCloseEditDrawer = useCallback(() => {
    if (isEditProfileDirty) {
      setShowDiscardEditConfirm(true);
      return;
    }
    setShowEditDrawer(false);
    clearEditProfileDraft();
    resetEditProfileDirty();
    syncProfileFromServer();
  }, [isEditProfileDirty, clearEditProfileDraft, resetEditProfileDirty, syncProfileFromServer]);

  const confirmDiscardEditDrawer = useCallback(() => {
    setShowDiscardEditConfirm(false);
    setShowEditDrawer(false);
    clearEditProfileDraft();
    resetEditProfileDirty();
    syncProfileFromServer();
  }, [clearEditProfileDraft, resetEditProfileDirty, syncProfileFromServer]);

  // When viewing another user and currentUserProfile is not yet loaded, fetch viewer profile so Send melding / Favoritt can show
  useEffect(() => {
    if (isOwnProfile || !accessToken || !currentUserId || currentUserProfile != null) {
      if (!currentUserId) setViewerProfile(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(
          `${API_BASE}/user-profile`,
          { headers: { Authorization: `Bearer ${accessToken}` } }
        );
        if (cancelled || !res.ok) return;
        const data = await res.json();
        setViewerProfile(data);
      } catch {
        if (!cancelled) setViewerProfile(null);
      }
    })();
    return () => { cancelled = true; };
  }, [isOwnProfile, accessToken, currentUserId, currentUserProfile]);

  const notifyProfileMutation = useCallback((reason: string) => {
    if (onProfileUpdate) {
      void onProfileUpdate();
    }
    window.dispatchEvent(new CustomEvent('talentfunn:profile-updated', { detail: { reason, userId, isOwnProfile } }));
    window.dispatchEvent(new CustomEvent('talentfunn:data-invalidated', { detail: { domain: 'profile', reason, userId } }));
  }, [onProfileUpdate, userId, isOwnProfile]);

  const notifyPostsMutation = useCallback((reason: string) => {
    window.dispatchEvent(new CustomEvent('talentfunn:posts-updated', { detail: { reason, userId } }));
    window.dispatchEvent(new CustomEvent('talentfunn:data-invalidated', { detail: { domain: 'posts', reason, userId } }));
  }, [userId]);

  const notifyPackagesMutation = useCallback((reason: string) => {
    window.dispatchEvent(new CustomEvent('talentfunn:packages-updated', { detail: { reason, userId } }));
    window.dispatchEvent(new CustomEvent('talentfunn:data-invalidated', { detail: { domain: 'packages', reason, userId } }));
  }, [userId]);

  const notifyFavoritesMutation = useCallback((reason: string) => {
    window.dispatchEvent(new CustomEvent('talentfunn:favorites-updated', { detail: { reason, userId } }));
    window.dispatchEvent(new CustomEvent('talentfunn:data-invalidated', { detail: { domain: 'favorites', reason, userId } }));
  }, [userId]);

  const fetchProfile = async () => {
    try {
      const response = await fetch(
        `${API_BASE}/profile/${userId}`
      );

      if (response.ok) {
        const data = await response.json();
        
        setCachedProfile(userId, data.profile);
        // Always apply fetch result so DB (e.g. new showcase) is shown when re-entering profile.
        // On own profile merge with currentUserProfile so private fields (email, etc.) are not lost.
        if (canApplyRemoteUpdate(showEditDrawer)) {
          if (isOwnProfile && currentUserProfile) {
            applyProfileToState({ ...currentUserProfile, ...data.profile });
          } else {
            applyProfileToState(data.profile);
          }
        }
        
        // Show welcome splash for new users (only on own profile, and only once)
        if (isOwnProfile && data.profile.userType === 'talent' && !hasCheckedWelcome.current) {
          hasCheckedWelcome.current = true; // Mark as checked to prevent re-triggering
          
          const hasSeenWelcome = localStorage.getItem(`welcomeSeen_${userId}`);
          const isNewlyVerified = localStorage.getItem(`isNewlyVerifiedUser_${userId}`);
          
          // ✅ FIXED: Only show welcome screen if user just verified their email
          // This ensures it only shows ONCE per user, not every time they have an empty profile
          if (!hasSeenWelcome && isNewlyVerified) {
            // Small delay to ensure profile is fully rendered before showing splash
            setTimeout(() => {
              setShowWelcomeSplash(true);
            }, 300);
            localStorage.setItem(`welcomeSeen_${userId}`, 'true');
            // Clear the "newly verified" flag now that we've shown the welcome screen
            localStorage.removeItem(`isNewlyVerifiedUser_${userId}`);
          }
        }
      }
    } catch (error) {
      devLog('Error fetching profile:', error);
    } finally {
      // Only set loading false if we didn't already set it in useEffect
      if (!isOwnProfile || !currentUserProfile) {
        setLoading(false);
      }
    }
  };

  const handleOpenEditDrawer = useCallback(() => {
    setShowEditDrawer(true);
  }, []);

  const fetchUserPosts = async () => {
    try {
      devLog(`🔵 fetchUserPosts called for userId: ${userId}`);
      setLoadingPosts(true);
      // Cache-bust so refetch after create/update/delete returns fresh data (server sends Cache-Control: max-age=60)
      const response = await fetch(
        `${API_BASE}/posts/user/${userId}?t=${Date.now()}`,
        {
          cache: 'no-store',
          headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
        }
      );

      devLog(`🔵 fetchUserPosts response status: ${response.status}`);

      if (response.ok) {
        const data = await response.json();
        devLog(`🔵 fetchUserPosts received ${data.posts?.length || 0} posts:`, data.posts);
        setPosts(data.posts || []);
        setFeaturedPublicPostId(data.featuredPublicPostId ?? null);
      } else {
        const errorData = await response.json().catch(() => ({}));
        devError('❌ fetchUserPosts failed', {
          status: response.status,
          requestId: errorData?.requestId ?? response.headers.get('X-Request-Id') ?? undefined,
          stage: errorData?.stage,
          error: errorData?.error,
        });
      }
    } catch (error) {
      devError('❌ Error fetching user posts:', error);
    } finally {
      setLoadingPosts(false);
    }
  };

  const fetchUserPackages = async () => {
    try {
      setLoadingPackages(true);
      const response = await fetch(`${API_BASE}/packages/user/${userId}?t=${Date.now()}`, {
        cache: 'no-store',
        headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        devError('Error fetching user packages', { status: response.status, error: data?.error });
        setPackages([]);
        setPackageRequestIds([]);
        return;
      }

      const data = await response.json();
      const nextPackages = data.packages || [];
      setPackages(nextPackages);
      const requestedIds = nextPackages
        .filter((item: any) => item?.hasInterest === true || item?.has_interest === true)
        .map((item: any) => item.id);
      setPackageRequestIds(requestedIds);
    } catch (error) {
      devError('Error fetching user packages:', error);
      setPackages([]);
      setPackageRequestIds([]);
    } finally {
      setLoadingPackages(false);
    }
  };

  const handleSetFeaturedPublicPost = async (postId: string) => {
    if (!accessToken) {
      toast.error('Du må være logget inn for å velge offentlig prosjekt');
      return;
    }

    try {
      const response = await fetch(`${API_BASE}/posts/${postId}/set-featured-public`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });

      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        toast.error(data?.error || 'Kunne ikke oppdatere offentlig prosjekt');
        return;
      }

      setFeaturedPublicPostId(postId);
      setPosts((prev) =>
        prev.map((post) => ({
          ...post,
          isFeaturedPublic: post.id === postId,
        }))
      );
      toast.success('Offentlig prosjekt oppdatert');
    } catch (error) {
      devError('Failed to set featured public post:', error);
      toast.error('Noe gikk galt. Prøv igjen senere.');
    }
  };

  const fetchUserDrafts = async () => {
    if (!accessToken || !isOwnProfile) return;
    try {
      const response = await fetch(`${API_BASE}/posts/drafts`, {
        headers: { Authorization: `Bearer ${accessToken}` },
        cache: 'no-store',
      });
      if (response.ok) {
        const data = await response.json();
        setDraftPosts(data.drafts ?? []);
      }
    } catch (error) {
      devError('Error fetching drafts:', error);
    }
  };

  const handleSendMessage = () => {
    // ✅ CRITICAL: Prevent execution if button should be disabled
    if (!onStartMessage || typeof onStartMessage !== 'function') {
      devError('❌ handleSendMessage: onStartMessage is not a function');
      return;
    }
    
    devLog('🔵 handleSendMessage called');
    const viewer = currentUserProfile ?? viewerProfile;
    devLog('🔵 effectiveViewerProfile:', viewer);
    devLog('🔵 userType:', viewer?.userType);
    devLog('🔵 messagingAccess:', viewer?.messagingAccess);
    devLog('🔵 subscription:', viewer?.subscription);
    devLog('🔵 isAdmin:', viewer?.isAdmin);
    devLog('🔵 verificationStatus:', viewer?.verificationStatus);
    
    // ⚠️ NEW: Check employer verification status first
    if (viewer?.userType === 'employer' && 
        viewer?.verificationStatus !== 'approved') {
      devLog('❌ Employer not verified - showing toast');
      
      const statusMessage = viewer?.verificationStatus === 'pending'
        ? 'Din bedriftskonto venter på godkjenning fra administrator. Du vil motta en e-post når kontoen er godkjent.'
        : 'Din bedriftskonto har blitt avvist. Ta kontakt med support for mer informasjon.';
      
      toast.info(statusMessage, { duration: 5000 });
      return;
    }
    
    // Check if user is employer and if they have messaging access
    // Employers get messaging access with 'employer' or 'employer_plus' subscription
    const hasMessagingAccess = hasEmployerMessagingAccess(viewer);
    
    if (viewer?.userType === 'employer' && !hasMessagingAccess) {
      devLog('Employer without messaging access - navigating to subscription page');
      // Navigate directly to subscription page instead of modal
      if (onNavigate) {
        onNavigate('subscription');
      }
      return;
    }

    // Talents must have Plus to start new conversations.
    if (viewer?.userType === 'talent' && !canTalentStartConversation(viewer)) {
      toast.info('Talentfunn Pluss kreves for å starte nye samtaler.');
      if (onNavigate) {
        onNavigate('subscription');
      }
      return;
    }
    
    devLog('✅ Access granted - opening message');
    // Admin or paid employers can proceed
    if (onStartMessage) {
      onStartMessage(userId);
    }
  };

  const handleOpenPackageRequest = (packageId: string) => {
    const pkg = packages.find((item) => item.id === packageId);
    if (!pkg) return;
    setRequestPackage(pkg);
  };

  const handlePackageRequestSuccess = () => {
    if (requestPackage?.id) {
      setPackageRequestIds((prev) => Array.from(new Set([...prev, requestPackage.id])));
    }
    void fetchUserPackages();
  };

  const handlePackageSaved = () => {
    notifyPackagesMutation('package-saved');
    void fetchUserPackages();
  };

  const handleClosePackage = async (packageId: string) => {
    if (!accessToken) return;
    try {
      const response = await fetch(`${API_BASE}/packages/${packageId}/close`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        toast.error(data?.error || 'Kunne ikke avpublisere tjeneste');
        return;
      }
      toast.success('Tjeneste avpublisert');
      notifyPackagesMutation('package-closed');
      void fetchUserPackages();
      setSelectedPackage((prev: any) => (prev?.id === packageId ? { ...prev, status: 'closed' } : prev));
    } catch (error) {
      toast.error('Kunne ikke avpublisere tjeneste');
    }
  };

  const handlePublishPackage = async (packageId: string) => {
    if (!accessToken) return;
    try {
      const response = await fetch(`${API_BASE}/packages/${packageId}/publish`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        if (data?.requiresUpgrade) {
          toast.error(data?.error || 'Oppgradering kreves');
          onNavigate?.('subscription');
          return;
        }
        toast.error(data?.error || 'Kunne ikke publisere tjeneste');
        return;
      }
      toast.success('Tjeneste publisert');
      notifyPackagesMutation('package-published');
      void fetchUserPackages();
      setSelectedPackage((prev: any) => (prev?.id === packageId ? { ...prev, status: 'active' } : prev));
    } catch (error) {
      toast.error('Kunne ikke publisere tjeneste');
    }
  };

  const handleDeletePackage = async (packageId: string) => {
    if (!accessToken) return;
    try {
      const response = await fetch(`${API_BASE}/packages/${packageId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        toast.error(data?.error || 'Kunne ikke slette tjeneste');
        return;
      }
      toast.success('Tjeneste slettet');
      notifyPackagesMutation('package-deleted');
      void fetchUserPackages();
      setSelectedPackage((prev: any) => (prev?.id === packageId ? null : prev));
    } catch (error) {
      toast.error('Kunne ikke slette tjeneste');
    }
  };

  const handleSaveProfile = async () => {
    if (!accessToken) {
      toast.error('Du må være logget inn for å lagre profilen');
      return;
    }
    
    try {
      // ✅ NEW: Validate for profanity before saving
      if (containsProfanity(name)) {
        toast.error('Navnet inneholder upassende språk. Vennligst bruk et profesjonelt språk.');
        return;
      }

      if (containsProfanity(bio)) {
        toast.error('Bio inneholder upassende språk. Vennligst bruk et profesjonelt språk.');
        return;
      }

      if (containsProfanity(jobWishes)) {
        toast.error('Jobbønsker inneholder upassende språk. Vennligst bruk et profesjonelt språk.');
        return;
      }

      if (containsProfanity(location)) {
        toast.error('By/Kommune inneholder upassende språk. Vennligst bruk et profesjonelt språk.');
        return;
      }

      // Validate skills array
      for (const skill of skills) {
        if (containsProfanity(skill)) {
          toast.error(`Ferdigheten "${skill}" inneholder upassende språk. Vennligst bruk et profesjonelt språk.`);
          return;
        }
      }

      setSavingProfile(true);
      const response = await fetch(
        `${API_BASE}/profile`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${accessToken}`,
          },
          body: JSON.stringify({ name, bio, location, county, skills, categories, jobWishes }),
        }
      );

      if (response.ok) {
        const data = await response.json();
        setProfile(data.profile);
        toast.success('Profilen din er oppdatert!');
        notifyProfileMutation('profile:save');
        // Scroll to top after saving
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } else {
        const errorData = await response.json();
        devLog('Error updating profile:', errorData);
        toast.error(errorData.error || 'Kunne ikke oppdatere profilen. Prøv igjen.');
      }
    } catch (error) {
      devLog('Error updating profile:', error);
      toast.error('Noe gikk galt. Prøv igjen senere.');
    } finally {
      setSavingProfile(false);
      setShowEditDrawer(false);
      clearEditProfileDraft();
      resetEditProfileDirty();
    }
  };
  
  const addSkill = () => {
    const trimmedSkill = skillInput.trim();
    
    if (!trimmedSkill) return;
    
    if (containsProfanity(trimmedSkill)) {
      toast.error('Ferdigheten inneholder upassende språk. Vennligst bruk et profesjonelt språk.');
      return;
    }
    
    if (skills.includes(trimmedSkill)) {
      toast.error('Du har allerede lagt til denne ferdigheten.');
      return;
    }
    
    setSkills([...skills, trimmedSkill]);
    markEditProfileDirty();
    setSkillInput('');
  };

  const removeSkill = (skillToRemove: string) => {
    setSkills(skills.filter((skill) => skill !== skillToRemove));
    markEditProfileDirty();
  };

  const handlePostCreated = () => {
    devLog('🔵 handlePostCreated called - closing modal and fetching posts...');
    setIsCreatingPost(false);
    setTimeout(() => {
      devLog('🔵 Fetching posts after 1 second delay...');
      fetchUserPosts();
      if (isOwnProfile && accessToken) fetchUserDrafts();
      notifyPostsMutation('posts:create');
    }, 1000);
  };

  const handlePostUpdated = () => {
    setEditingPost(null);
    setTimeout(() => {
      fetchUserPosts();
      if (isOwnProfile && accessToken) fetchUserDrafts();
      notifyPostsMutation('posts:update');
    }, 1000);
  };

  const handleDeletePost = async (postId: string) => {
    devLog('handleDeletePost called', { postId, settingShowDeleteConfirm: true });
    setPostToDelete(postId);
    setShowDeleteConfirm(true);
  };

  const confirmDeletePost = async () => {
    if (!postToDelete) return;
    
    if (!accessToken) {
      toast.error('Du må være logget inn for å slette prosjekter');
      return;
    }

    setDeletingPost(true);
    try {
      const response = await fetch(
        `${API_BASE}/posts/${postToDelete}`,
        {
          method: 'DELETE',
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );

      if (!response.ok) {
        const data = await response.json();
        toast.error(`Kunne ikke slette prosjekt: ${data.error ||'Noe gikk galt'}`);
        return;
      }

      // Refresh posts list and drafts (own profile)
      fetchUserPosts();
      if (isOwnProfile) fetchUserDrafts();
      notifyPostsMutation('posts:delete');
      toast.success('Prosjektet ble slettet');
    } catch (error) {
      devLog('Error deleting post:', error);
      toast.error('Noe gikk galt. Prøv igjen senere.');
    } finally {
      setDeletingPost(false);
      setShowDeleteConfirm(false);
      setPostToDelete(null);
    }
  };

  useEffect(() => {
    devLog('Delete dialog state changed', { showDeleteConfirm, postToDelete });
  }, [showDeleteConfirm, postToDelete]);

  const confirmDeletePackage = async () => {
    if (!packageToDelete) return;
    if (!accessToken) {
      toast.error('Du må være logget inn for å slette tjenester');
      return;
    }
    setDeletingPackage(true);
    try {
      await handleDeletePackage(packageToDelete);
      setShowDeletePackageConfirm(false);
      setPackageToDelete(null);
    } finally {
      setDeletingPackage(false);
    }
  };

  const checkFavoriteStatus = async () => {
    if (!accessToken) return;
    
    try {
      const response = await fetch(
        `${API_BASE}/favorites/check/${userId}`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        setIsFavorited(data.isFavorited);
      }
    } catch (error) {
      devLog('Error checking favorite status:', error);
    }
  };

  const toggleFavorite = async () => {
    // ✅ CRITICAL: Validate callback functions before execution
    if (!onNavigate || typeof onNavigate !== 'function') {
      devError('❌ toggleFavorite: onNavigate is not a function');
      return;
    }
    
    if (!accessToken) {
      if (onLoginRequired) onLoginRequired();
      return;
    }
    
    setTogglingFavorite(true);
    try {
      const response = await fetch(
        `${API_BASE}/favorites/toggle/${userId}`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        setIsFavorited(data.isFavorited);
        notifyFavoritesMutation('favorites:toggle');
      } else {
        const errorData = await response.json();
        handleApiError(errorData, () => {
          onNavigate?.('subscription');
        });
      }
    } catch (error) {
      devLog('Error toggling favorite:', error);
      // Prevent unhandled errors from propagating
    } finally {
      setTogglingFavorite(false);
    }
  };

  const trackProfileView = async () => {
    if (!accessToken) {
      devLog('⏭️ Skipping profile view tracking - no access token');
      return;
    }
    
    try {
      await fetch(
        `${API_BASE}/profile/views/${userId}`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );
    } catch (error) {
      devLog('Error tracking view:', error);
    }
  };

  const fetchProfileStats = async () => {
    if (!accessToken) return;
    
    try {
      const response = await fetch(
        `${API_BASE}/profile/stats/${userId}`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        setProfileStats(data.stats);
      }
    } catch (error) {
      devLog('Error fetching profile stats:', error);
    }
  };

  const handleShareProfile = () => {
    setShowShareModal(true);
  };

  const handleReportProfile = async () => {
    if (!reportReason.trim()) {
      toast.error('Velg en årsak for rapporten');
      return;
    }
    
    if (!accessToken) {
      toast.error('Du må være logget inn for å rapportere');
      return;
    }
    
    try {
      setSendingReport(true);
      const response = await fetch(
        `${API_BASE}/report`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${accessToken}`,
          },
          body: JSON.stringify({
            reportedUserId: userId,
            reason: reportReason,
            description: reportDescription,
          }),
        }
      );

      if (response.ok) {
        setShowReportModal(false);
        setReportReason('');
        setReportDescription('');
        toast.success('Rapportering sendt', 'Takk for tilbakemeldingen');
      } else {
        const errorData = await response.json().catch(() => null);
        toast.error(errorData?.error || 'Kunne ikke sende rapport');
      }
    } catch (error) {
      devLog('Error reporting profile:', error);
      toast.error('Noe gikk galt');
    } finally {
      setSendingReport(false);
    }
  };

  const fetchBlockedUsers = async () => {
    if (!accessToken) {
      devLog('⏭️ Skipping fetch blocked users - no access token');
      return;
    }
    
    setLoadingBlockedUsers(true);
    try {
      const response = await fetch(
        `${API_BASE}/blocked`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        setBlockedUsers(data.blockedUsers || []);
      }
    } catch (error) {
      devLog('Error fetching blocked users:', error);
    } finally {
      setLoadingBlockedUsers(false);
    }
  };

  const handleUnblockUser = async (blockedUserId: string) => {
    if (!accessToken) {
      toast.error('Du må være logget inn');
      return;
    }
    
    try {
      setUnblockingUserId(blockedUserId);
      const response = await fetch(
        `${API_BASE}/unblock`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${accessToken}`,
          },
          body: JSON.stringify({
            userId: blockedUserId,
          }),
        }
      );

      if (response.ok) {
        // Remove user from blocked list
        setBlockedUsers(prev => prev.filter(u => u.id !== blockedUserId));
        toast.success('Blokkering opphevet');
      } else {
        toast.error('Kunne ikke oppheve blokkering');
      }
    } catch (error) {
      devLog('Error unblocking user:', error);
      toast.error('Noe gikk galt');
    } finally {
      setUnblockingUserId(null);
    }
  };

  // Don't block the whole profile on secondary requests like posts.
  if (loading) {
    return (
      <div className="fixed inset-0 z-10 flex flex-col items-center justify-center text-center bg-transparent">
        <Loader size="lg" className="text-purple-600" />
        <p className="mt-4">Laster profil...</p>
      </div>
    );
  }

  // If profile is employer, show employer view
  if (profile?.userType === 'employer') {
    return (
      <EmployerProfileView
        userId={userId}
        accessToken={accessToken}
        isOwnProfile={isOwnProfile}
        currentUserProfile={currentUserProfile}
        isDesktop={isDesktop}
        onViewFavorites={onViewFavorites}
        onViewSavedSearches={onViewSavedSearches}
        onSignOut={onSignOut}
        onNavigate={onNavigate}
        onCreateGig={onCreateGig}
        onProfileUpdate={onProfileUpdate}
      />
    );
  }

  if (isCreatingPost && isOwnProfile) {
    const isPlusUser = canUploadVideo(profile);
    const maxPosts = isPlusUser ? 6 : 1;
    const canCreatePost = posts.length < maxPosts;

    if (!canCreatePost) {
      // Different messages for Plus vs Free users
      if (isPlusUser) {
        // Plus user at max (5 projects)
        return (
          <PageShell className="max-w-3xl mx-auto px-4 sm:px-6 xl:px-8 pt-12 xl:pb-12">
            <div className="bg-gradient-to-br from-purple-600 via-pink-600 to-orange-600 rounded-3xl shadow-2xl p-12 text-white text-center">
              <AlertTriangle className="w-20 h-20 mx-auto mb-6" />
              <h2 className="text-3xl mb-4">Maks 6 prosjekter nådd</h2>
              <p className="text-purple-100 mb-8 text-lg">
                Du har nådd maksgrensen på 6 portfolioprosjekter.
                <br />
                Slett et eksisterende prosjekt for å legge til et nytt.
              </p>
              <button
                onClick={() => setIsCreatingPost(false)}
                className="px-8 py-4 bg-white text-purple-600 rounded-2xl hover:shadow-2xl transition-all transform hover:scale-105"
              >
                Tilbake til profil
              </button>
            </div>
          </PageShell>
        );
      } else {
        // Free user at max (1 project)
        return (
          <PageShell className="max-w-3xl mx-auto px-4 sm:px-6 xl:px-8 pt-12 xl:pb-12">
            <div className="bg-gradient-to-br from-purple-600 via-pink-600 to-orange-600 rounded-3xl shadow-2xl p-12 text-white text-center">
              <Lock className="hidden sm:block w-20 h-20 mx-auto mb-6" />
              <h2 className="text-3xl mb-4">Gratis-grensen nådd</h2>
              <p className="text-purple-100 mb-8 text-lg">
                Du har nådd maksgrensen på 1 portfolioprosjekt for gratis-brukere.
                <br />
                Oppgrader til Pluss for inntil 6 prosjekter med video!
              </p>
              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center items-stretch sm:items-center w-full max-w-md mx-auto">
                <button
                  onClick={() => {
                    setIsCreatingPost(false);
                    onNavigate?.('subscription');
                  }}
                  className="w-full sm:w-auto px-6 sm:px-8 py-4 bg-white text-purple-600 rounded-2xl hover:shadow-2xl transition-all transform hover:scale-105 flex items-center justify-center gap-2 text-center"
                >
                  <Crown className="w-5 h-5" />
                  Oppgrader til Pluss
                </button>
                <button
                  onClick={() => setIsCreatingPost(false)}
                  className="w-full sm:w-auto px-6 sm:px-8 py-4 bg-white/20 text-white rounded-2xl border border-white/40 hover:bg-white/30 transition-all text-center"
                >
                  Avbryt
                </button>
              </div>
            </div>
          </PageShell>
        );
      }
    }

    return (
      <CreatePostPage
        accessToken={accessToken}
        onPostCreated={handlePostCreated}
        onCancel={() => setIsCreatingPost(false)}
        onNavigateToSubscription={onNavigate ? () => onNavigate('subscription') : undefined}
      />
    );
  }

  if (editingPost && isOwnProfile) {
    return (
      <EditPostPage
        post={editingPost}
        accessToken={accessToken}
        onPostUpdated={handlePostUpdated}
        onCancel={() => setEditingPost(null)}
        onNavigateToSubscription={onNavigate ? () => onNavigate('subscription') : undefined}
      />
    );
  }

  if (activePackageInterests && accessToken && !isDesktop) {
    return (
      <TalentPackageInterestsView
        packageId={activePackageInterests.id}
        packageTitle={activePackageInterests.title}
        accessToken={accessToken}
        onBack={() => setActivePackageInterests(null)}
        onViewProfile={() => {}}
        onStartConversation={(targetUserId) => {
          setActivePackageInterests(null);
          onStartMessage?.(targetUserId);
        }}
      />
    );
  }

  if (selectedPost) {
    return (
      <PostDetailView
        post={selectedPost}
        onClose={() => setSelectedPost(null)}
        accessToken={accessToken}
        currentUserId={currentUserProfile?.id}
        isOwnPost={selectedPost.userId === currentUserProfile?.id}
      />
    );
  }

  const editDrawerHeader = (
    <>
      <div className="flex items-center gap-2 min-w-0">
        <button
          onClick={requestCloseEditDrawer}
          className="xl:hidden p-2 hover:bg-gray-100 rounded-lg transition-colors"
          aria-label="Tilbake"
        >
          <ArrowLeft className="w-5 h-5 text-gray-700" />
        </button>
        <h2 className="text-lg xl:text-xl font-bold text-purple-900 xl:font-semibold xl:text-gray-900 truncate">Rediger profil</h2>
      </div>
      <button
        onClick={requestCloseEditDrawer}
        className="hidden xl:flex p-2 hover:bg-gray-100 rounded-full transition-colors"
        aria-label="Lukk"
      >
        <X className="w-6 h-6 text-gray-600" />
      </button>
    </>
  );

  const editDrawerEl = (
      <AnimatePresence>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.18, ease: 'easeOut' }}
          className={`min-h-screen bg-white xl:bg-transparent xl:fixed xl:inset-0 xl:talentfunn-modal-overlay xl:backdrop-blur-md flex items-start xl:items-center justify-center p-0 xl:p-4 xl:pt-4 xl:pb-4 ${(isIosPwa || isAndroid) ? 'fixed inset-0 flex-col overflow-hidden' : 'xl:overflow-y-auto'}`}
          style={{ zIndex: 'var(--z-modal)' }}
        >
          {/* iOS PWA: fixed header + spacer (samme mønster som CreateGigModal) */}
          {isIosPwa && (
            <>
              <div
                className="xl:hidden fixed left-0 right-0 top-0 z-20 flex-shrink-0 min-h-[3.5rem] bg-white border-b border-gray-200 px-4 pb-3 flex items-center justify-between rounded-none"
                style={{ paddingTop: 'calc(env(safe-area-inset-top, 0px) + 0.5rem)' }}
              >
                {editDrawerHeader}
              </div>
              <div
                className="xl:hidden flex-shrink-0 edit-drawer-spacer"
                aria-hidden="true"
              />
            </>
          )}
          {/* Android (nettleser + PWA): fast header + spacer – headeren manglet i nettleser uten dette */}
          {isAndroid && (
            <>
              <div
                className="xl:hidden fixed left-0 right-0 top-0 z-20 flex-shrink-0 min-h-[3.5rem] bg-white border-b border-gray-200 px-4 pb-3 flex items-center justify-between rounded-none"
                style={{ paddingTop: `calc(env(safe-area-inset-top, 0px) + ${androidHeaderPadTop})` }}
              >
                {editDrawerHeader}
              </div>
              <div
                className="xl:hidden flex-shrink-0"
                style={{ height: `calc(env(safe-area-inset-top, 0px) + ${androidHeaderPadTop} + 3.5rem + 0.75rem)` }}
                aria-hidden="true"
              />
            </>
          )}
          <motion.div
            initial={{ y: 20, opacity: 0.98 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 20, opacity: 0.98 }}
            transition={{ duration: 0.2, ease: 'easeOut' }}
            className={`edit-drawer-shell bg-white w-full min-h-screen xl:min-h-0 rounded-none xl:rounded-2xl max-w-none xl:max-w-2xl xl:max-h-[85vh] shadow-none xl:shadow-2xl xl:overflow-y-auto xl:pt-0 ${(isIosPwa || isAndroid) ? 'flex-1 min-h-0 max-h-[100dvh] overflow-y-auto flex flex-col' : ''}`}
          >
            {/* Header – på iOS PWA og Android vises headeren i overlay; på desktop (xl) alltid her */}
            <div className={`sub-page-header sticky top-0 z-20 flex-shrink-0 min-h-[3.5rem] xl:min-h-0 bg-white border-b border-gray-200 px-4 pt-4 pb-3 xl:px-6 xl:pt-4 xl:pb-4 flex items-center justify-between rounded-none xl:rounded-t-2xl ${(isIosPwa || isAndroid) ? 'hidden xl:flex' : ''}`}>
              {editDrawerHeader}
            </div>
            {/* iOS PWA / Android: wrapper med ios-scroll mellom inner og content */}
            {(() => {
              const Wrapper = (isIosPwa || isAndroid) ? 'div' : Fragment;
              const wrapperProps = (isIosPwa || isAndroid) ? { className: 'overflow-x-hidden ios-scroll flex-1 min-h-0' } : {};
              return (
                <Wrapper {...wrapperProps}>
            <div className="edit-profile-drawer-content edit-drawer-content-bottom px-6 py-6 space-y-6 pb-8 xl:pb-8">
              {/* Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Brukernavn
                </label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => { setName(e.target.value); markEditProfileDirty(); }}
                  maxLength={80}
                  className="w-full border-2 border-purple-300 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-purple-500"
                  placeholder="Ditt navn"
                />
              </div>

              {/* County */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Fylke
                </label>
                <select
                  value={county}
                  onChange={(e) => { setCounty(e.target.value); markEditProfileDirty(); }}
                  className="w-full border-2 border-purple-300 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-purple-500 bg-white"
                >
                  <option value="">Velg fylke</option>
                  {NORWEGIAN_COUNTIES.map((c) => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
              </div>

              {/* Location */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  By/Kommune
                </label>
                <input
                  type="text"
                  value={location}
                  onChange={(e) => { setLocation(e.target.value); markEditProfileDirty(); }}
                  maxLength={50}
                  className="w-full border-2 border-purple-300 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-purple-500"
                  placeholder="F.eks. Oslo, Bergen, Trondheim..."
                />
              </div>

              {/* Bio */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Om meg
                </label>
                <textarea
                  value={bio}
                  onChange={(e) => { setBio(e.target.value); markEditProfileDirty(); }}
                  maxLength={1000}
                  className="w-full border-2 border-purple-300 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-purple-500"
                  rows={4}
                  placeholder="Fortell litt om deg selv..."
                />
              </div>

              {/* Job Wishes */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Jobbønsker
                </label>
                <textarea
                  value={jobWishes}
                  onChange={(e) => { setJobWishes(e.target.value); markEditProfileDirty(); }}
                  maxLength={150}
                  className="w-full border-2 border-purple-300 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-purple-500"
                  rows={3}
                  placeholder="Hva slags jobber ser du etter?"
                />
              </div>

              {/* Skills */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Ferdigheter
                </label>
                <div className="flex gap-2 mb-3">
                  <input
                    type="text"
                    value={skillInput}
                    onChange={(e) => setSkillInput(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addSkill())}
                    maxLength={40}
                    className="flex-1 border-2 border-purple-300 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-purple-500"
                    placeholder="F.eks. 'Elektriker', 'React', 'Fotograf'..."
                  />
                  <button
                    onClick={addSkill}
                    className="px-6 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-xl hover:shadow-md transition-all whitespace-nowrap"
                  >
                    <Plus className="w-5 h-5" />
                  </button>
                </div>
                <p className="text-xs text-gray-500 mb-3 flex items-center gap-1.5">
                  <Lightbulb className="w-3.5 h-3.5 text-yellow-600" />
                  Alle ferdigheter du legger til blir automatisk søkbare med relaterte begreper.
                </p>
                {/* Selected skills */}
                <div className="flex flex-wrap gap-2">
                  {skills.length > 0 ? (
                    skills.map((skill, index) => (
                      <span
                        key={index}
                        className="px-3 py-1.5 bg-gradient-to-r from-purple-50 to-pink-50 text-purple-700 rounded-lg border border-purple-200 flex items-center gap-2 text-sm"
                      >
                        {skill}
                        <button
                          onClick={() => removeSkill(skill)}
                          className="text-purple-700 hover:text-purple-900 hover:bg-purple-200 rounded-full p-0.5"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </span>
                    ))
                  ) : (
                    <p className="text-gray-500 text-sm">Ingen ferdigheter lagt til ennå</p>
                  )}
                </div>
              </div>
              
              {/* Action Buttons - Scrollable at bottom of content */}
              <div className="pt-6 border-t-2 border-purple-200">
                <div className="flex gap-2 pb-safe">
                  <button
                    onClick={requestCloseEditDrawer}
                    className="flex-1 px-6 py-3 bg-gray-200 text-gray-700 rounded-xl hover:bg-gray-300 transition-all"
                  >
                    Avbryt
                  </button>
                  <button
                    onClick={() => handleSaveProfile()}
                    disabled={savingProfile}
                    className="flex-1 px-6 py-3 bg-purple-600 text-white rounded-xl hover:bg-purple-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {savingProfile ? 'Lagrer endringer...' : 'Lagre endringer'}
                  </button>
                </div>
              </div>
            </div>
                </Wrapper>
              );
            })()}
          </motion.div>
        </motion.div>
      </AnimatePresence>
  );
  if (showEditDrawer && isOwnProfile && !isDesktop) return editDrawerEl;

  const avatarColors = [
    'from-purple-500 to-pink-500',
    'from-blue-500 to-cyan-500',
    'from-orange-500 to-red-500',
    'from-green-500 to-emerald-500',
  ];
  
  const getAvatarColor = (name: string) => {
    const index = name?.charCodeAt(0) % avatarColors.length || 0;
    return avatarColors[index];
  };

  const handleProfileImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!accessToken) {
      toast.error('Du må være logget inn for å laste opp bilder');
      return;
    }

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Vennligst velg en bildefil');
      return;
    }

    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Bildet er for stort. Maksimal størrelse er 10MB.');
      return;
    }

    setUploadingProfileImage(true);

    try {
      const croppedFile = await openCrop(file, AVATAR_IMAGE_SPEC);
      const base64String = await compressImageForUpload(croppedFile, PROFILE_SHOWCASE_PRESET);

      const response = await fetch(
        `${API_BASE}/profile/upload-image`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${accessToken}`,
          },
          body: JSON.stringify({ imageBase64: base64String }),
        }
      );

      const data = await response.json();

      if (response.ok) {
        setProfile(prev => ({ ...prev, profileImage: data.profileImage }));
        setCachedProfile(userId, { ...profile, profileImage: data.profileImage });
        setShowProfileImageOptions(false);
        toast.success('Profilbilde oppdatert!');
        notifyProfileMutation('profile:image-upload');
      } else {
        toast.error(data.error || 'Kunne ikke laste opp profilbilde');
      }
    } catch (error) {
      if (error instanceof Error && error.message.includes('avbrutt')) return;
      devLog('Error uploading profile image:', error);
      toast.error('Noe gikk galt. Prøv igjen senere.');
    } finally {
      setUploadingProfileImage(false);
    }
  };

  const handleDeleteProfileImage = async () => {
    if (!accessToken) {
      toast.error('Du må være logget inn for å slette bilder');
      return;
    }
    
    setUploadingProfileImage(true);

    try {
      const response = await fetch(
        `${API_BASE}/profile/delete-image`,
        {
          method: 'DELETE',
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );

      if (response.ok) {
        setProfile(prev => (prev ? { ...prev, profileImage: null } : prev));
        setCachedProfile(userId, profile ? { ...profile, profileImage: null } : profile);
        setShowProfileImageOptions(false);
        toast.success('Profilbilde slettet!');
        notifyProfileMutation('profile:image-delete');
      } else {
        const data = await response.json();
        toast.error(data.error || 'Kunne ikke slette profilbilde');
      }
    } catch (error) {
      devLog('Error deleting profile image:', error);
      toast.error('Noe gikk galt. Prøv igjen senere.');
    } finally {
      setUploadingProfileImage(false);
    }
  };

  const handleShowcaseImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!accessToken) {
      toast.error('Du må være logget inn for å laste opp bilder');
      return;
    }

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Vennligst velg en bildefil');
      return;
    }

    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Bildet er for stort. Maksimal størrelse er 10MB.');
      return;
    }

    setUploadingShowcaseImage(true);

    try {
      const croppedFile = await openCrop(file, CONTENT_IMAGE_SPEC_4_3);
      const base64String = await compressImageForUpload(croppedFile, PROFILE_SHOWCASE_PRESET);

      const response = await fetch(
        `${API_BASE}/profile/upload-showcase-image`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${accessToken}`,
          },
          body: JSON.stringify({ imageBase64: base64String }),
        }
      );

      let data: { showcaseImage?: string; showcase_image?: string; error?: string } = {};
      try {
        data = await response.json();
      } catch {
        toast.error('Kunne ikke laste opp showcase-bilde. Prøv igjen senere.');
        setUploadingShowcaseImage(false);
        return;
      }

      const imageUrl = data?.showcaseImage ?? data?.showcase_image;
      if (response.ok && imageUrl) {
        setProfile(prev => ({ ...prev, showcaseImage: imageUrl }));
        setCachedProfile(userId, { ...profile, showcaseImage: imageUrl });
        toast.success('Showcase-bilde oppdatert!');
        notifyProfileMutation('profile:showcase-upload');
      } else if (response.ok && !imageUrl) {
        toast.error(data.error || 'Showcase-bildet ble ikke lagret. Prøv igjen.');
      } else {
        toast.error(data.error || 'Kunne ikke laste opp showcase-bilde');
      }
    } catch (error) {
      if (error instanceof Error && error.message.includes('avbrutt')) return;
      devLog('Error uploading showcase image:', error);
      toast.error('Noe gikk galt. Prøv igjen senere.');
    } finally {
      setUploadingShowcaseImage(false);
    }
  };

  const handleDeleteShowcaseImage = async () => {
    if (!accessToken) {
      toast.error('Du må være logget inn for å slette bilder');
      return;
    }
    
    setUploadingShowcaseImage(true);

    try {
      const response = await fetch(
        `${API_BASE}/profile/delete-showcase-image`,
        {
          method: 'DELETE',
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );

      if (response.ok) {
        setProfile(prev => (prev ? { ...prev, showcaseImage: null } : prev));
        setCachedProfile(userId, profile ? { ...profile, showcaseImage: null } : profile);
        toast.success('Showcase-bilde fjernet!');
        notifyProfileMutation('profile:showcase-delete');
      } else {
        let data: { error?: string } = {};
        try {
          data = await response.json();
        } catch {
          toast.error('Kunne ikke fjerne showcase-bilde. Prøv igjen senere.');
          return;
        }
        toast.error(data.error || 'Kunne ikke fjerne showcase-bilde');
      }
    } catch (error) {
      devLog('Error deleting showcase image:', error);
      toast.error('Kunne ikke fjerne showcase-bilde. Prøv igjen senere.');
    } finally {
      setUploadingShowcaseImage(false);
    }
  };

  return (
    <>
      {showEditDrawer && isOwnProfile && isDesktop && ReactDOM.createPortal(editDrawerEl, document.body)}
    <div className="min-h-screen bg-white xl:bg-gradient-to-br xl:from-purple-50 xl:via-pink-50 xl:to-blue-50">
        {/* Wrapper - aligned with global header on mobile and desktop */}
        <PageShell className="pt-page-safe-sm xl:max-w-4xl xl:mx-auto xl:px-4 xl:px-8 xl:pt-5 xl:pb-24">
      
      {/* Content container - edge to edge on mobile, rounded modal on desktop */}
      <div className="bg-white xl:rounded-3xl xl:shadow-xl xl:border-2 xl:border-purple-100 xl:hover:shadow-2xl transition-shadow relative z-20" style={{ isolation: 'isolate' }}>
      {/* Profile Header - Edge to edge on mobile, compact on desktop */}
      
        {/* Settings icon - Absolute positioned to top right */}
        {isOwnProfile && accessToken && !showWelcomeSplash && !showEditDrawer && !showSettingsSheet && (
          <button
            onClick={() => setShowSettingsSheet(true)}
            className="absolute top-4 right-4 bg-white bg-opacity-95 backdrop-blur-sm hover:bg-opacity-100 p-2.5 rounded-full shadow-lg transition-all active:scale-95 z-50"
          >
            <Settings className="w-5 h-5 text-gray-700" />
          </button>
        )}
        
        {/* Cover gradient - Compact height */}
        <div className="h-24 xl:h-32 bg-gradient-to-r from-purple-600 to-pink-600 relative xl:rounded-t-3xl overflow-hidden z-0">
          {/* Online status badge on cover */}
          {profile?.lastActive && !isOwnProfile && !profile?.hideOnlineStatus && (
            <div className="absolute top-4 right-4 z-10">
              {formatLastActive(profile.lastActive).includes('Aktiv nå') ? (
                <div 
                  className="px-3 py-1.5 rounded-full flex items-center gap-1.5 shadow-lg"
                  style={{
                    background: 'linear-gradient(135deg, #9333ea 0%, #ec4899 100%)'
                  }}
                >
                  <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse shadow-sm"></div>
                  <span className="text-white text-xs font-medium">Aktiv nå</span>
                </div>
              ) : (
                <div className="bg-white bg-opacity-90 px-3 py-1.5 rounded-full flex items-center gap-1.5 shadow-lg">
                  <Clock className="w-3.5 h-3.5 text-gray-600" />
                  <span className="text-gray-600 text-xs">{formatLastActive(profile.lastActive)}</span>
                </div>
              )}
            </div>
          )}
        
        </div>

        <div className="px-4 sm:px-8 pb-6 sm:pb-8">
          {/* Avatar and basic info - Centered on mobile */}
          <div className="flex flex-col mb-6 relative z-30">
            {/* Avatar - centered on mobile with adjusted negative margin */}
            <div className="flex flex-col items-center sm:items-start sm:flex-row sm:gap-6 -mt-16 sm:-mt-12 mb-4">
              <div className="relative group">
                {profile?.profileImage ? (
                  <img
                    src={profile.profileImage}
                    alt={profile.name}
                    className="w-28 h-28 sm:w-32 sm:h-32 rounded-full border-4 border-white shadow-xl flex-shrink-0 object-cover bg-white"
                    loading="eager"
                    draggable={false}
                    onContextMenu={(e) => e.preventDefault()}
                    onDragStart={(e) => e.preventDefault()}
                  />
                ) : (
                  <div className="w-28 h-28 sm:w-32 sm:h-32 bg-gradient-to-br from-purple-600 to-pink-600 rounded-full flex items-center justify-center border-4 border-white shadow-xl flex-shrink-0">
                    <User className="w-12 h-12 sm:w-14 sm:h-14 text-white" />
                  </div>
                )}
                {isOwnProfile && accessToken && (
                  <button
                    onClick={() => setShowProfileImageOptions(!showProfileImageOptions)}
                    className="absolute bottom-0 right-0 bg-purple-600 text-white p-2.5 rounded-full shadow-lg hover:bg-purple-700 transition-all"
                    disabled={uploadingProfileImage}
                  >
                    <Camera className="w-4 h-4" />
                  </button>
                )}
                {showProfileImageOptions && isOwnProfile && (
                  <div className="absolute left-1/2 -translate-x-1/2 sm:left-0 sm:translate-x-0 top-full mt-2 bg-white rounded-2xl shadow-2xl border-2 border-purple-200 p-3 z-10 min-w-[200px]">
                    <label className="flex items-center gap-2 px-4 py-2 hover:bg-purple-50 rounded-lg cursor-pointer transition-all">
                      <Upload className="w-4 h-4 text-purple-600" />
                      <span className="text-sm text-gray-700">
                        {profile?.profileImage ? 'Endre bilde' : 'Last opp bilde'}
                      </span>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleProfileImageUpload}
                        className="hidden"
                        disabled={uploadingProfileImage}
                      />
                    </label>
                    {profile?.profileImage && (
                      <button
                        onClick={handleDeleteProfileImage}
                        disabled={uploadingProfileImage}
                        className="flex items-center gap-2 px-4 py-2 hover:bg-red-50 rounded-lg transition-all w-full text-left"
                      >
                        <Trash2 className="w-4 h-4 text-red-600" />
                        <span className="text-sm text-red-600">Slett bilde</span>
                      </button>
                    )}
                    {uploadingProfileImage && (
                      <div className="px-4 py-2 text-sm text-gray-500">
                        Laster opp...
                      </div>
                    )}
                  </div>
                )}
              </div>
              {/* Profile info - centered on mobile */}
              <div className="text-center sm:text-left sm:pb-2 sm:pt-16 mt-2 sm:mt-0">
                <div className="flex flex-col sm:flex-row sm:flex-wrap items-center sm:items-baseline gap-2 mb-2">
                  <h1 className="text-2xl sm:text-3xl font-semibold text-gray-900">
                    {profile?.name}
                  </h1>
                  {canUploadVideo(profile) && (
                    <div className="flex items-center gap-1 px-2.5 py-1 bg-yellow-500 rounded-full shadow-sm">
                      <Crown className="w-3.5 h-3.5 text-white" />
                      <span className="text-white text-xs font-medium">PLUSS</span>
                    </div>
                  )}
                </div>
                <>
                  {(profile?.location || profile?.county) && (
                    <div className="flex items-center justify-center sm:justify-start gap-1.5 text-gray-600 mb-1">
                      <MapPin className="w-4 h-4" />
                      <span className="text-sm sm:text-base">
                        {[profile?.location, profile?.county].filter(Boolean).join(', ')}
                      </span>
                    </div>
                  )}
                  {!isOwnProfile && (
                    <div className="flex items-center justify-center sm:justify-start gap-1.5 text-gray-500">
                      <Clock className="w-3.5 h-3.5" />
                      <span className="text-sm">{formatLastActive(profile?.lastActive)}</span>
                    </div>
                  )}
                </>
              </div>
            </div>
            
            {/* Action buttons - Native mobile style */}
            <div className="flex flex-col gap-2 mt-2">
              {!isOwnProfile && (
                <>
                  {/* Show send message when viewing a talent profile (admin, employer, or talent viewer). */}
                  {accessToken && onStartMessage && 
                   (
                     effectiveViewerProfile?.isAdmin === true ||
                     effectiveViewerProfile?.userType === 'employer' ||
                     effectiveViewerProfile?.userType === 'talent'
                   ) && 
                   profile?.userType === 'talent' && (() => {
                     const hasMessagingAccess = hasEmployerMessagingAccess(effectiveViewerProfile);
                     const isEmployerDisabled = effectiveViewerProfile?.userType === 'employer' && !hasMessagingAccess;
                     const isTalentDisabled = effectiveViewerProfile?.userType === 'talent' && !canTalentStartConversation(effectiveViewerProfile);
                     const isDisabled = isEmployerDisabled || isTalentDisabled;
                     
                     return (
                       <button
                        onClick={isDisabled ? () => onNavigate?.('subscription') : handleSendMessage}
                        disabled={false}
                         className={`w-full xl:w-auto flex items-center justify-center gap-2 px-6 py-3 rounded-xl transition-all font-medium min-h-[48px] ${
                           isDisabled 
                            ? 'bg-gray-400 text-gray-200 cursor-pointer hover:bg-gray-500' 
                             : 'bg-gradient-to-r from-purple-600 to-pink-600 text-white hover:from-purple-700 hover:to-pink-700 shadow-md hover:shadow-lg'
                         }`}
                       >
                         <MessageSquare className="w-5 h-5" />
                         {isDisabled
                          ? isTalentDisabled
                            ? 'Oppgrader til Talentfunn Pluss for å sende melding'
                            : 'Bli abonnent for å sende melding'
                           : 'Send melding'}
                       </button>
                     );
                   })()}
                  {/* Only show favorite button for employers, not talents */}
                  {effectiveViewerProfile?.userType === 'employer' && profile?.userType === 'talent' && (() => {
                    const hasFavoritesAccess = canUseFavorites(effectiveViewerProfile);
                    const isDisabled = !hasFavoritesAccess && !isFavorited;
                    
                    return (
                      <button
                        onClick={isDisabled ? () => onNavigate?.('subscription') : toggleFavorite}
                        disabled={togglingFavorite}
                        className={`w-full xl:w-auto flex items-center justify-center gap-2 px-6 py-3 rounded-xl transition-all font-medium min-h-[48px] ${
                          isDisabled
                            ? 'bg-gray-400 text-gray-200 cursor-pointer hover:bg-gray-500'
                            : togglingFavorite
                              ? 'bg-gray-400 text-gray-200 cursor-wait'
                              : isFavorited 
                                ? 'bg-gradient-to-r from-pink-600 to-red-600 text-white hover:from-pink-700 hover:to-red-700 shadow-md hover:shadow-lg'
                                : 'bg-gradient-to-r from-purple-600 to-pink-600 text-white hover:from-purple-700 hover:to-pink-700 shadow-md hover:shadow-lg'
                        }`}
                      >
                        {isDisabled ? <Lock className="w-5 h-5" /> : togglingFavorite ? (
                          <Loader size="sm" className="text-white" />
                        ) : (
                          <Heart className="w-5 h-5" />
                        )}
                        {isDisabled 
                          ? 'Oppgrader for favoritter' 
                          : togglingFavorite 
                            ? (isFavorited ? 'Fjerner...' : 'Legger til...') 
                            : isFavorited 
                              ? 'Favoritt' 
                              : 'Legg til favoritt'
                        }
                      </button>
                    );
                  })()}
                </>
              )}
            </div>
          </div>

          {/* Bio Section - Part of unified container */}
          <div className="px-4 sm:px-8 py-5 border-b border-gray-200">
            <h3 className="mb-3 text-gray-900 font-semibold text-base">
              Om meg
            </h3>
            <>
              <LinkifiedText
                text={(() => {
                  const bioText = profile?.bio || 'Ingen beskrivelse ennå';
                  const bioPreviewLength = 500;
                  const isBioLong = bioText.length > bioPreviewLength;
                  if (!isBioLong || bioExpanded) return bioText;
                  return bioText.substring(0, bioPreviewLength) + '...';
                })()}
                className="text-gray-700 leading-relaxed"
              />
              {profile?.bio && profile.bio.length > 500 && (
                  <button
                    onClick={() => setBioExpanded(!bioExpanded)}
                    className="mt-2 text-purple-600 font-medium hover:underline text-sm flex items-center gap-1"
                  >
                    {bioExpanded ? 'Vis mindre' : 'Les mer'}
                  </button>
                )}
              </>
          </div>

          {/* Job Wishes Section - Part of unified container */}
          <div className="px-4 sm:px-8 py-5 border-b border-gray-200">
            <h3 className="mb-3 text-gray-900 font-semibold text-base">
              Jobbønsker
            </h3>
            <LinkifiedText
              text={profile?.jobWishes || 'Ingen jobbønsker lagt til ennå'}
              className="text-gray-700 leading-relaxed"
            />
          </div>

          {/* Skills Section - Part of unified container */}
          <div className="px-4 sm:px-8 py-5 border-b border-gray-200">
            <h3 className="mb-4 text-gray-900 font-semibold text-base">
              Ferdigheter
            </h3>
            {/* Selected skills - Matching HomePage style */}
            <div className="flex flex-wrap gap-2">
              {skills.length > 0 ? (
                skills.map((skill, index) => (
                  <span
                    key={index}
                    className="px-3 py-1.5 bg-gradient-to-r from-purple-50 to-pink-50 text-purple-700 rounded-lg border border-purple-200 flex items-center gap-2 text-sm"
                  >
                    {skill}
                  </span>
                ))
              ) : (
                <p className="text-gray-600">Ingen ferdigheter lagt til ennå</p>
              )}
            </div>
            
            {/* Share and Report buttons */}
            {!isOwnProfile && (
              <div className="flex flex-wrap gap-3 mt-4">
                <button
                  onClick={handleShareProfile}
                  className="flex items-center gap-2 px-6 py-3 bg-gray-200 text-gray-800 rounded-xl hover:bg-gray-300 transition-all font-medium"
                >
                  <Share2 className="w-5 h-5" />
                  Del profil
                </button>
                {accessToken && (
                  <button
                    onClick={() => setShowReportModal(true)}
                    className="flex items-center gap-2 px-6 py-3 border-2 border-orange-200 text-orange-600 rounded-xl hover:bg-orange-50 transition-all font-medium"
                  >
                    <AlertTriangle className="w-5 h-5" />
                    Rapporter
                  </button>
                )}
              </div>
            )}
          </div>

          {/* Showcase Image Section - Only on own profile */}
          {isOwnProfile && (
            <div className="px-4 sm:px-8 py-5 border-b border-gray-200">
              <h3 className="mb-4 text-gray-900 font-semibold text-base">
                Showcase-bilde
              </h3>
              {uploadingShowcaseImage && (
                <div className="mb-3 px-4 py-2 text-sm text-gray-600 font-medium">
                  Laster opp bilde...
                </div>
              )}
              {profile?.showcaseImage ? (
                // Has showcase image - show it with edit/delete options
                <div className="space-y-3">
                  <div className="relative aspect-[4/3] w-full rounded-lg overflow-hidden border-2 border-purple-200">
                    <img 
                      src={profile.showcaseImage} 
                      alt="Showcase" 
                      className="w-full h-full object-cover"
                      draggable={false}
                      onContextMenu={(e) => e.preventDefault()}
                      onDragStart={(e) => e.preventDefault()}
                    />
                  </div>
                  <div className="flex gap-2">
                    <label className="flex-1 px-4 py-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg hover:shadow-md transition-all text-sm font-medium cursor-pointer text-center">
                      <Camera className="w-4 h-4 inline mr-2" />
                      Endre bilde
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleShowcaseImageUpload}
                        className="hidden"
                        disabled={uploadingShowcaseImage}
                      />
                    </label>
                    <button
                      onClick={handleDeleteShowcaseImage}
                      disabled={uploadingShowcaseImage}
                      className="px-4 py-2 border border-red-300 text-red-600 rounded-lg hover:bg-red-50 transition-all disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium"
                    >
                      <Trash2 className="w-4 h-4 inline mr-2" />
                      Fjern
                    </button>
                  </div>
                </div>
              ) : (
                // No showcase image - show placeholder with explanation
                <div className="space-y-3">
                  <label className="relative aspect-[4/3] w-full rounded-lg overflow-hidden border-2 border-dashed border-purple-300 bg-gradient-to-br from-purple-50 to-pink-50 cursor-pointer hover:border-purple-400 transition-all group block">
                    <div className="absolute inset-0 flex flex-col items-center justify-center p-6 text-center">
                      <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mb-3 group-hover:scale-110 transition-transform">
                        <Camera className="w-8 h-8 text-purple-600" />
                      </div>
                      <p className="text-sm text-purple-800 font-medium mb-1">
                        Dette er hovedbildet som vises på hovedfeed/søk.
                      </p>
                      <p className="text-xs text-purple-600">
                        Velg et bilde som viser det du er mest stolt av!
                      </p>
                    </div>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleShowcaseImageUpload}
                      className="hidden"
                      disabled={uploadingShowcaseImage}
                    />
                  </label>
                  <label className="w-full px-4 py-2.5 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg hover:shadow-md transition-all text-sm font-medium cursor-pointer block text-center">
                    <Plus className="w-4 h-4 inline mr-2" />
                    Legg til showcase-bilde
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleShowcaseImageUpload}
                      className="hidden"
                      disabled={uploadingShowcaseImage}
                    />
                  </label>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      
      {/* Visibility Warning Banner */}
      {isOwnProfile && profile?.userType === 'talent' && (() => {
        const hasCompleteProject = !!posts?.some((p: any) => (p.coverImageUrl || p.cover_image_url) && (p.title || '').trim() && (p.description || '').trim());
        const isProfileComplete = !!(profile?.bio?.trim() && profile?.skills?.length && profile?.showcaseImage && hasCompleteProject);
        return (
        <>
          {!isProfileComplete ? (
            // ⚠️ INCOMPLETE PROFILE - Show checklist
            <div className="mx-4 mt-4 mb-4 bg-gradient-to-r from-purple-50 to-pink-50 rounded-lg p-4 xl:mx-0">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-purple-600 flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="text-base font-semibold text-purple-900 mb-1">
                    Profil ikke synlig
                  </h3>
                  <p className="text-sm text-purple-700 mb-3">
                    For å bli synlig må du:
                  </p>
                  <ul className="space-y-1.5 mb-3 text-sm">
                    <li className="flex items-center gap-2">
                      {profile?.bio && profile.bio.trim() ? (
                        <CheckCircle className="w-5 h-5 text-green-600" />
                      ) : (
                        <div className="w-5 h-5 rounded-full border-2 border-purple-400" />
                      )}
                      <span className={profile?.bio && profile.bio.trim() ? 'text-green-800' : 'text-purple-800'}>
                        Legge til "Om meg" beskrivelse
                      </span>
                    </li>
                    <li className="flex items-center gap-2">
                      {profile?.skills && profile.skills.length > 0 ? (
                        <CheckCircle className="w-5 h-5 text-green-600" />
                      ) : (
                        <div className="w-5 h-5 rounded-full border-2 border-purple-400" />
                      )}
                      <span className={profile?.skills && profile.skills.length > 0 ? 'text-green-800' : 'text-purple-800'}>
                        Legge til minst én ferdighet
                      </span>
                    </li>
                    <li className="flex items-center gap-2">
                      {profile?.showcaseImage ? (
                        <CheckCircle className="w-5 h-5 text-green-600" />
                      ) : (
                        <div className="w-5 h-5 rounded-full border-2 border-purple-400" />
                      )}
                      <span className={profile?.showcaseImage ? 'text-green-800' : 'text-purple-800'}>
                        Last opp et showcase-bilde
                      </span>
                    </li>
                    <li className="flex items-center gap-2">
                      {hasCompleteProject ? (
                        <CheckCircle className="w-5 h-5 text-green-600" />
                      ) : (
                        <div className="w-5 h-5 rounded-full border-2 border-purple-400" />
                      )}
                      <span className={hasCompleteProject ? 'text-green-800' : 'text-purple-800'}>
                        Publiser et komplett prosjekt
                      </span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          ) : (
            // ✅ COMPLETE PROFILE - Show success message
            <div className="mx-4 mt-4 mb-4 bg-gradient-to-r from-purple-50 to-pink-50 rounded-lg p-4 xl:mx-0">
              <div className="flex items-center gap-3">
                <CheckCircle className="w-5 h-5 text-purple-600 flex-shrink-0" />
                <p className="text-sm text-purple-800 font-medium">
                  Din profil er nå synlig i søk
                </p>
              </div>
            </div>
          )}
        </>
        );
      })()}

      {/* Portfolio section - Redesigned */}
      <div className="px-4 sm:px-8 mt-8 mb-12 xl:mt-8">
        <div className="flex items-center justify-between mb-6 gap-2">
          <h2 className="text-xl sm:text-2xl font-semibold text-gray-900">
            {isOwnProfile
              ? 'Mine prosjekter'
              : `${profile?.name || 'Bruker'}${(profile?.name || '').trim().toLowerCase().endsWith('s') ? "'" : "'s"} prosjekter`}
          </h2>
          {isOwnProfile && (
            <button
              onClick={() => setIsCreatingPost(true)}
              className="flex items-center gap-1.5 px-4 py-2.5 bg-purple-600 text-white rounded-xl hover:bg-purple-700 transition-all font-medium"
            >
              <Plus className="w-5 h-5" />
              <span className="hidden sm:inline">Legg til</span>
            </button>
          )}
        </div>

        {/* ⚠️ FREE USER WARNING - Show for own profile if subscription is free */}
        {isOwnProfile && profile?.subscription === 'free' && posts.length > 1 && (
          <div className="mb-4 p-4 bg-amber-50 border border-amber-200 rounded-xl">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm font-medium text-amber-900 mb-1">
                  Kun 1 prosjekt vises i søkeresultater
                </p>
                <p className="text-sm text-amber-700">
                  Du har {posts.length} prosjekter. Som Free-bruker kan du velge hvilket 1 prosjekt som vises offentlig.
                </p>
              </div>
            </div>
          </div>
        )}
        
        {loadingPosts ? (
          <div className="text-center py-12 bg-gray-50 rounded-lg border border-gray-200">
            <Loader size={64} className="text-purple-600 mx-auto mb-3" />
            <p className="text-gray-600">Laster prosjekter...</p>
          </div>
        ) : posts.length === 0 && (!isOwnProfile || draftPosts.length === 0) ? (
          <div className="text-center py-12 bg-gray-50 rounded-lg border border-gray-200">
            <div className="w-16 h-16 bg-gray-200 rounded-full flex items-center justify-center mx-auto mb-3">
              <Briefcase className="w-8 h-8 text-gray-400" />
            </div>
            <p className="text-gray-700 font-medium mb-1">
              {isOwnProfile
                ? 'Ingen prosjekter ennå'
                : 'Ingen prosjekter'}
            </p>
            {isOwnProfile && (
              <>
                <p className="text-gray-500 text-sm mb-4">Vis alle hva du kan!</p>
                <button
                  onClick={() => setIsCreatingPost(true)}
                  className="px-5 py-2.5 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-all font-medium"
                >
                  Legg til prosjekt
                </button>
              </>
            )}
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 xl:grid-cols-3 gap-4 sm:gap-6">
              {(() => {
                // Own profile: show published first, then drafts (drafts have isDraft: true from API)
                const visiblePosts = isOwnProfile
                  ? [...posts, ...draftPosts]
                  : (!isOwnProfile && profile?.subscription === 'free')
                    ? posts.slice(0, 1)
                    : posts;
                return visiblePosts.map((post) => (
                  <ProjectCardWithActions
                    key={post.id}
                    post={{ ...post, isDraft: post.isDraft ?? false, isFeaturedPublic: post.id === featuredPublicPostId }}
                    onView={() => setSelectedPost(post)}
                    onEdit={() => setEditingPost(post)}
                    onDelete={() => handleDeletePost(post.id)}
                    onSetPublic={
                      isOwnProfile &&
                      profile?.subscription === 'free' &&
                      !post.isDraft
                        ? () => handleSetFeaturedPublicPost(post.id)
                        : undefined
                    }
                    isOwnProfile={isOwnProfile}
                    accessToken={accessToken}
                  />
                ));
              })()}
            </div>
            
            {/* ⚠️ INFO: Show hidden projects notice for visitors viewing free user profile */}
            {!isOwnProfile && profile?.subscription === 'free' && posts.length > 1 && (
              <div className="mt-4 p-3 bg-gray-50 border border-gray-200 rounded-xl text-center">
                <p className="text-sm text-gray-600">
                  Denne brukeren har {posts.length} prosjekter totalt, men viser kun 1 offentlig som Free-bruker.
                </p>
              </div>
            )}
          </>
        )}
      </div>

      {/* Services section */}
      {(() => {
        const allPackages = packages || [];
        const activePackages = allPackages.filter((item) => item?.status === 'active');
        const visiblePackages = isOwnProfile ? allPackages : activePackages;
        const canManageServices = isOwnProfile && canCreateTalentPackage(profile);
        const shouldShowSection = isOwnProfile
          ? (visiblePackages.length > 0 || canManageServices)
          : activePackages.length > 0;
        if (!shouldShowSection) return null;
        const ownerName = profile?.name || 'Bruker';
        const suffix = ownerName.trim().toLowerCase().endsWith('s') ? "'" : "'s";
        const sectionTitle = isOwnProfile ? 'Mine tjenester' : `${ownerName}${suffix} tjenester`;

        return (
          <div className="px-4 sm:px-8 mt-8 mb-12 xl:mt-8">
            <div className="flex items-center justify-between mb-6 gap-2">
              <h2 className="text-xl sm:text-2xl font-semibold text-gray-900">{sectionTitle}</h2>
              {canManageServices && (
                <button
                  onClick={() => setCreatingPackage(true)}
                  className="flex items-center gap-1.5 px-4 py-2.5 bg-purple-600 text-white rounded-xl hover:bg-purple-700 transition-all font-medium"
                >
                  <Plus className="w-5 h-5" />
                  <span className="hidden sm:inline">Legg til</span>
                </button>
              )}
            </div>

            {loadingPackages ? (
              <div className="text-center py-12 bg-gray-50 rounded-lg border border-gray-200">
                <Loader size={64} className="text-purple-600 mx-auto mb-3" />
                <p className="text-gray-600">Laster tjenester...</p>
              </div>
            ) : visiblePackages.length === 0 ? (
              <div className="text-center py-12 bg-gray-50 rounded-lg border border-gray-200">
                <div className="w-16 h-16 bg-gray-200 rounded-full flex items-center justify-center mx-auto mb-3">
                  <Briefcase className="w-8 h-8 text-gray-400" />
                </div>
                <p className="text-gray-700 font-medium mb-1">Ingen tjenester ennå</p>
                {canManageServices && (
                  <>
                    <p className="text-gray-500 text-sm mb-4">Vis alle hva du tilbyr!</p>
                    <button
                      onClick={() => setCreatingPackage(true)}
                      className="px-5 py-2.5 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-all font-medium"
                    >
                      Legg til tjeneste
                    </button>
                  </>
                )}
              </div>
            ) : (
              <div className="grid grid-cols-2 xl:grid-cols-3 gap-4 sm:gap-6">
                {visiblePackages.map((packageItem: any) => (
                  <TalentPackageCard
                    key={packageItem.id}
                    packageItem={packageItem}
                    isOwnPackage={isOwnProfile}
                    hasRequested={packageRequestIds.includes(packageItem.id)}
                    onViewDetails={(packageId) => {
                      const target = visiblePackages.find((item: any) => item.id === packageId);
                      if (target) setSelectedPackage(target);
                    }}
                    onSendRequest={handleOpenPackageRequest}
                    onViewRequests={(packageId, title) => setActivePackageInterests({ id: packageId, title })}
                    onEdit={isOwnProfile ? (packageId) => {
                      const target = visiblePackages.find((item: any) => item.id === packageId);
                      setSelectedPackage(null);
                      setEditingPackage(target || null);
                    } : undefined}
                    onDelete={isOwnProfile ? (packageId) => {
                      setPackageToDelete(packageId);
                      setShowDeletePackageConfirm(true);
                    } : undefined}
                  />
                ))}
              </div>
            )}
          </div>
        );
      })()}

      {/* Edit post / post detail are handled as full-page takeover above */}

      {/* Share Modal */}
      {showShareModal && ReactDOM.createPortal(
        <div className="fixed inset-0 talentfunn-modal-overlay backdrop-blur-md flex items-center justify-center p-4 pt-safe pb-24 pb-safe" style={{ zIndex: 'var(--z-modal)' }}>
          <div className="bg-white rounded-3xl shadow-2xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent pb-1">
                Del profil
              </h3>
              <button
                onClick={() => setShowShareModal(false)}
                className="p-2 hover:bg-purple-50 rounded-full transition-all"
              >
                <X className="w-5 h-5 text-gray-600" />
              </button>
            </div>
            
            <p className="text-gray-600 mb-6">
              {isOwnProfile 
                ? 'Del din profil på sosiale medier eller kopier linken'
                : `Del ${profile?.name || 'denne'}s profil på sosiale medier eller kopier linken`
              }
            </p>
            
            {/* Social Media Buttons */}
            <div className="space-y-3 mb-6">
              {/* Facebook */}
              <button
                onClick={() => {
                  const profileUrl = encodeURIComponent(`https://${window.location.host}?profile=${userId}`);
                  window.open(`https://www.facebook.com/sharer/sharer.php?u=${profileUrl}`, '_blank');
                }}
                className="w-full flex items-center gap-3 px-4 py-3 bg-[#1877F2] text-white rounded-xl hover:bg-[#1665D8] transition-all"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                </svg>
                <span className="font-medium">Del på Facebook</span>
              </button>
              
              {/* LinkedIn */}
              <button
                onClick={() => {
                  const profileUrl = encodeURIComponent(`https://${window.location.host}?profile=${userId}`);
                  window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${profileUrl}`, '_blank');
                }}
                className="w-full flex items-center gap-3 px-4 py-3 bg-[#0A66C2] text-white rounded-xl hover:bg-[#004182] transition-all"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                </svg>
                <span className="font-medium">Del på LinkedIn</span>
              </button>
              
              {/* Twitter/X */}
              <button
                onClick={() => {
                  const profileUrl = encodeURIComponent(`https://${window.location.host}?profile=${userId}`);
                  const text = encodeURIComponent(isOwnProfile ? 'Sjekk ut min profil på Talentfunn!' : `Sjekk ut ${profile?.name || 'denne profilen'} på Talentfunn!`);
                  window.open(`https://twitter.com/intent/tweet?url=${profileUrl}&text=${text}`, '_blank');
                }}
                className="w-full flex items-center gap-3 px-4 py-3 bg-black text-white rounded-xl hover:bg-gray-800 transition-all"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                </svg>
                <span className="font-medium">Del på X (Twitter)</span>
              </button>
              
              {/* WhatsApp */}
              <button
                onClick={() => {
                  const profileUrl = encodeURIComponent(`https://${window.location.host}?profile=${userId}`);
                  const text = encodeURIComponent(isOwnProfile ? 'Sjekk ut min profil på Talentfunn!' : `Sjekk ut ${profile?.name || 'denne profilen'} på Talentfunn!`);
                  window.open(`https://wa.me/?text=${text}%20${profileUrl}`, '_blank');
                }}
                className="w-full flex items-center gap-3 px-4 py-3 bg-[#25D366] text-white rounded-xl hover:bg-[#1DA851] transition-all"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                </svg>
                <span className="font-medium">Del på WhatsApp</span>
              </button>
            </div>
            
            {/* Copy Link */}
            <div className="border-t border-gray-200 pt-4">
              <label className="block text-sm text-gray-600 mb-2">Eller kopier linken</label>
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  value={`https://${window.location.host}?profile=${userId}`}
                  readOnly
                  className="flex-1 px-3 py-2 border-2 border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                />
                <button
                  onClick={async () => {
                    const profileUrl = `https://${window.location.host}?profile=${userId}`;
                    
                    try {
                      await navigator.clipboard.writeText(profileUrl);
                      toast.success('Link kopiert!');
                    } catch (err) {
                      const textArea = document.createElement('textarea');
                      textArea.value = profileUrl;
                      textArea.style.position = 'fixed';
                      textArea.style.left = '-999999px';
                      document.body.appendChild(textArea);
                      textArea.select();
                      try {
                        document.execCommand('copy');
                        toast.success('Link kopiert!');
                      } catch (e) {
                        toast.error('Kunne ikke kopiere');
                      }
                      document.body.removeChild(textArea);
                    }
                  }}
                  className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-all text-sm font-medium"
                >
                  Kopier
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Report Profile Modal */}
      {showReportModal && ReactDOM.createPortal(
        <div className="fixed inset-0 talentfunn-modal-overlay backdrop-blur-md flex items-center justify-center p-4 pt-safe pb-24 pb-safe" style={{ zIndex: 'var(--z-modal)' }}>
          <div className="bg-white rounded-3xl shadow-2xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent pb-1">
                Rapporter profil
              </h3>
              <button
                onClick={() => setShowReportModal(false)}
                className="p-2 hover:bg-purple-50 rounded-full transition-all"
              >
                <X className="w-5 h-5 text-gray-600" />
              </button>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-gray-700 mb-2">Årsak</label>
                <select
                  value={reportReason}
                  onChange={(e) => setReportReason(e.target.value)}
                  className="w-full px-4 py-3 border-2 border-purple-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500"
                >
                  <option value="">Velg årsak</option>
                  <option value="spam">Spam</option>
                  <option value="harassment">Trakassering</option>
                  <option value="inappropriate">Upassende innhold</option>
                  <option value="fake">Falsk profil</option>
                  <option value="other">Annet</option>
                </select>
              </div>
              
              <div>
                <label className="block text-gray-700 mb-2">Beskrivelse (valgfritt)</label>
                <textarea
                  value={reportDescription}
                  onChange={(e) => setReportDescription(e.target.value)}
                  placeholder="Beskriv problemet..."
                  rows={4}
                  className="w-full px-4 py-3 border-2 border-purple-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 resize-none"
                />
              </div>
              
              <div className="flex gap-3">
                <button
                  onClick={() => setShowReportModal(false)}
                  className="flex-1 px-6 py-3 border-2 border-purple-200 text-purple-600 rounded-xl hover:bg-purple-50 transition-all"
                >
                  Avbryt
                </button>
                <button
                  onClick={handleReportProfile}
                  disabled={!reportReason || sendingReport}
                  className="flex-1 px-6 py-3 bg-gradient-to-r from-orange-500 to-red-500 text-white rounded-xl hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {sendingReport ? 'Sender...' : 'Send rapport'}
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Blocked Users Modal */}
      {showBlockedUsers && ReactDOM.createPortal(
        <div className="fixed inset-0 talentfunn-modal-overlay backdrop-blur-md flex items-center justify-center p-4 pt-safe pb-24 pb-safe" style={{ zIndex: 'var(--z-modal)' }}>
          <div className="bg-white rounded-3xl shadow-2xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent pb-1">
                Blokkerte brukere
              </h3>
              <button
                onClick={() => setShowBlockedUsers(false)}
                className="p-2 hover:bg-purple-50 rounded-full transition-all"
              >
                <X className="w-5 h-5 text-gray-600" />
              </button>
            </div>
            
            <div className="space-y-3 max-h-[60vh] overflow-y-auto">
              {loadingBlockedUsers ? (
                <div className="text-center py-8">
                  <Loader size="md" className="text-purple-600" />
                  <p className="mt-2 text-gray-600">Laster...</p>
                </div>
              ) : blockedUsers.length === 0 ? (
                <div className="text-center py-8">
                  <Ban className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-600">Ingen blokkerte brukere</p>
                </div>
              ) : (
                blockedUsers.map((user) => (
                  <div key={user.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
                    <div className="flex items-center gap-3">
                      {user.profileImage ? (
                        <img
                          src={user.profileImage}
                          alt={user.name}
                          className="w-12 h-12 rounded-full object-cover"
                        />
                      ) : (
                        <div className="w-12 h-12 bg-gradient-to-br from-purple-600 to-pink-600 rounded-full flex items-center justify-center">
                          <span className="text-white font-medium">
                            {user.name?.charAt(0).toUpperCase()}
                          </span>
                        </div>
                      )}
                      <div>
                        <p className="font-medium text-gray-900">{user.name}</p>
                        {user.location && (
                          <p className="text-sm text-gray-500">{user.location}</p>
                        )}
                      </div>
                    </div>
                    <button
                      onClick={() => handleUnblockUser(user.id)}
                      disabled={unblockingUserId === user.id}
                      className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-all text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {unblockingUserId === user.id ? 'Opphever...' : 'Opphev'}
                    </button>
                  </div>
                ))
              )}
            </div>
            
            <button
              onClick={() => setShowBlockedUsers(false)}
              className="w-full mt-4 px-6 py-3 border-2 border-purple-200 text-purple-600 rounded-xl hover:bg-purple-50 transition-all"
            >
              Lukk
            </button>
          </div>
        </div>,
        document.body
      )}

      {/* Profile Stats - Native style */}
      {isOwnProfile && profileStats && (
        <div className="px-4 sm:px-8 py-4 mb-6 bg-white border-t border-gray-200">
          <div className="grid grid-cols-2 gap-3">
            <div className="bg-gray-50 rounded-lg p-3 text-center">
              <div className="text-2xl font-bold text-gray-900">{profileStats.views}</div>
              <div className="text-xs text-gray-600 mt-1">Visninger</div>
            </div>
            <div className="bg-gray-50 rounded-lg p-3 text-center">
              <div className="text-2xl font-bold text-gray-900">{profileStats.favorites}</div>
              <div className="text-xs text-gray-600 mt-1">Favoritter</div>
            </div>
          </div>
        </div>
      )}

      {/* Welcome Splash for New Users */}
      {showWelcomeSplash && (
        <>
          {/* Backdrop */}
          <div 
            className="fixed inset-0 talentfunn-modal-overlay backdrop-blur-md" 
            style={{ zIndex: 'var(--z-overlay)' }}
            onClick={() => setShowWelcomeSplash(false)}
          />
          {/* Modal */}
          <div className="fixed inset-0 flex items-center justify-center p-4 pt-safe pb-24 pb-safe overflow-y-auto ios-scroll pointer-events-none" style={{ zIndex: 'var(--z-overlay)' }}>
            <div className="bg-white rounded-3xl shadow-2xl max-w-2xl w-full p-4 sm:p-8 xl:p-12 relative overflow-hidden my-auto max-h-[65vh] sm:max-h-[75vh] overflow-y-auto ios-scroll pointer-events-auto">
              <div className="relative">
                {/* Welcome header */}
                <div className="text-center mb-4 sm:mb-6">
                  <div className="inline-flex items-center justify-center w-12 h-12 sm:w-16 sm:h-16 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full mb-2 sm:mb-3">
                    <Sparkles className="w-6 h-6 sm:w-8 sm:h-8 text-white" />
                  </div>
                  <h2 className="text-xl sm:text-2xl xl:text-3xl bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent pb-1 mb-1 sm:mb-2 text-center leading-tight break-words">
                    Velkommen til Talentfunn! <span className="inline-block align-middle">🎉</span>
                  </h2>
                  <p className="text-sm sm:text-base text-gray-600">
                    Gratulerer med din nye profil!
                  </p>
                </div>

                {/* Steps */}
                <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-2xl p-3 sm:p-5 mb-4 sm:mb-5 border-2 border-purple-200">
                  <h3 className="text-base sm:text-lg text-purple-900 mb-2 sm:mb-3">
                    Følg disse stegene:
                  </h3>
                  <div className="space-y-2 sm:space-y-3">
                    <div className="flex items-start gap-2">
                      <div className="w-6 h-6 sm:w-7 sm:h-7 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full flex items-center justify-center text-white text-xs sm:text-sm flex-shrink-0">
                        1
                      </div>
                      <div>
                        <p className="text-gray-800 font-medium text-xs sm:text-sm">Legg til "Om meg" beskrivelse</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-2">
                      <div className="w-6 h-6 sm:w-7 sm:h-7 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full flex items-center justify-center text-white text-xs sm:text-sm flex-shrink-0">
                        2
                      </div>
                      <div>
                        <p className="text-gray-800 font-medium text-xs sm:text-sm">Legg til minst én ferdighet</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-2">
                      <div className="w-6 h-6 sm:w-7 sm:h-7 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full flex items-center justify-center text-white text-xs sm:text-sm flex-shrink-0">
                        3
                      </div>
                      <div>
                        <p className="text-gray-800 font-medium text-xs sm:text-sm">Last opp et showcase-bilde</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-2">
                      <div className="w-6 h-6 sm:w-7 sm:h-7 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full flex items-center justify-center text-white text-xs sm:text-sm flex-shrink-0">
                        4
                      </div>
                      <div>
                        <p className="text-gray-800 font-medium text-xs sm:text-sm">Last opp dine beste prosjekter</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Call to action */}
                <div className="text-center">
                  <button
                    onClick={() => {
                      setShowWelcomeSplash(false);
                      handleOpenEditDrawer();
                    }}
                    className="px-6 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-full hover:shadow-xl transition-all transform hover:scale-105 text-sm sm:text-base font-medium"
                  >
                    Kom i gang nå!
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}


      {/* Settings Sheet - Native mobile drawer */}
      <AnimatePresence>
      {showSettingsSheet && isOwnProfile && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setShowSettingsSheet(false)}
            className="fixed inset-0 talentfunn-modal-overlay backdrop-blur-md"
            style={{ zIndex: 'var(--z-overlay)' }}
          />
          
          {/* Sheet - bottom drawer on mobile, centered modal on desktop */}
          <motion.div 
            initial={{ y: "100%", opacity: 0, scale: 0.95 }}
            animate={{ y: 0, opacity: 1, scale: 1 }}
            exit={{ y: "100%", opacity: 0, scale: 0.95 }}
            transition={{ type: "spring", damping: 30, stiffness: 300 }}
            className="fixed bottom-[-1px] left-0 right-0 bg-white rounded-t-3xl shadow-2xl z-50 max-h-[70vh] overflow-y-auto ios-scroll xl:bottom-auto xl:top-1/2 xl:left-1/2 xl:-translate-x-1/2 xl:-translate-y-1/2 xl:rounded-2xl xl:max-w-md xl:w-full border-t-2 border-gray-200 xl:border-0"
          >
            {/* Handle bar - Mobile only */}
            <div className="flex justify-center pt-3 pb-2 xl:hidden">
              <div className="w-12 h-1 bg-gray-300 rounded-full" />
            </div>
            
            {/* Header */}
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-semibold text-gray-900">Innstillinger</h2>
                <button
                  onClick={() => setShowSettingsSheet(false)}
                  className="p-2 hover:bg-gray-100 rounded-full transition-all"
                >
                  <X className="w-6 h-6 text-gray-600" />
                </button>
              </div>
            </div>
            
            {/* Settings options */}
            <div className="px-6 py-4">
              <div className="space-y-2">
                {/* Edit Profile */}
                <button
                  onClick={() => {
                    setShowSettingsSheet(false);
                    handleOpenEditDrawer();
                  }}
                  className="w-full flex items-center gap-3 px-4 py-3.5 hover:bg-gray-100 rounded-xl transition-all text-left"
                >
                  <Edit2 className="w-5 h-5 text-gray-700" />
                  <span className="font-medium text-gray-900">Rediger profil</span>
                </button>
                
                {/* Mine oppdrag (for employer_plus users) */}
                {canCreateGig(profile) && onNavigate && (
                  <button
                    onClick={() => {
                      setShowSettingsSheet(false);
                      onNavigate('my-gigs');
                    }}
                    className="w-full flex items-center gap-3 px-4 py-3.5 hover:bg-gray-100 rounded-xl transition-all text-left"
                  >
                    <Briefcase className="w-5 h-5 text-gray-700" />
                    <span className="font-medium text-gray-900">Mine oppdrag</span>
                  </button>
                )}

                {/* Aktuelle oppdrag (plus / employer / employer_plus) */}
                {canShowGigInterest(profile) && onNavigate && (
                  <button
                    onClick={() => {
                      setShowSettingsSheet(false);
                      onNavigate('my-interests');
                    }}
                    className="w-full flex items-center gap-3 px-4 py-3.5 hover:bg-gray-100 rounded-xl transition-all text-left"
                  >
                    <NotebookText className="w-5 h-5 text-gray-700" />
                    <span className="font-medium text-gray-900">Aktuelle oppdrag</span>
                  </button>
                )}
                
                {/* Blocked Users */}
                <button
                  onClick={() => {
                    setShowSettingsSheet(false);
                    setShowBlockedUsers(true);
                    fetchBlockedUsers();
                  }}
                  className="w-full flex items-center gap-3 px-4 py-3.5 hover:bg-gray-100 rounded-xl transition-all text-left"
                >
                  <Ban className="w-5 h-5 text-gray-700" />
                  <span className="font-medium text-gray-900">Blokkerte brukere</span>
                </button>
                
                {/* Subscription */}
                {canUploadVideo(profile) && onNavigate && (
                  <button
                    onClick={() => {
                      setShowSettingsSheet(false);
                      onNavigate('subscription');
                    }}
                    className="w-full flex items-center gap-3 px-4 py-3.5 hover:bg-gray-100 rounded-xl transition-all text-left"
                  >
                    <Crown className="w-5 h-5 text-yellow-600" />
                    <span className="font-medium text-gray-900">Administrer abonnement</span>
                  </button>
                )}
              </div>
            </div>
          </motion.div>
        </>
      )}
      </AnimatePresence>

      {selectedPackage && (
        <TalentPackageDetailModal
          packageItem={selectedPackage}
          hasRequested={packageRequestIds.includes(selectedPackage.id)}
          isOwnPackage={isOwnProfile}
          onClose={() => setSelectedPackage(null)}
          onSendRequest={handleOpenPackageRequest}
          onEdit={(packageId) => {
            const target = packages.find((item) => item.id === packageId);
            setSelectedPackage(null);
            setEditingPackage(target || null);
          }}
          onClosePackage={handleClosePackage}
          onPublishPackage={handlePublishPackage}
          onDeletePackage={handleDeletePackage}
          onViewRequests={(packageId) => {
            const target = packages.find((item) => item.id === packageId);
            setSelectedPackage(null);
            setActivePackageInterests({ id: packageId, title: target?.title || 'Tjeneste' });
          }}
        />
      )}

      {requestPackage && accessToken && (
        <ShowPackageRequestModal
          packageId={requestPackage.id}
          packageTitle={requestPackage.title || 'Tjeneste'}
          accessToken={accessToken}
          userProfile={effectiveViewerProfile}
          onClose={() => setRequestPackage(null)}
          onSuccess={handlePackageRequestSuccess}
          onNavigateToSubscription={onNavigate ? () => onNavigate('subscription') : undefined}
        />
      )}

      {(creatingPackage || editingPackage) && accessToken && (
        <CreateTalentPackageModal
          accessToken={accessToken}
          userProfile={profile}
          initialPackage={editingPackage}
          onClose={() => {
            setCreatingPackage(false);
            setEditingPackage(null);
          }}
          onSuccess={handlePackageSaved}
          onNavigateToSubscription={onNavigate ? () => onNavigate('subscription') : undefined}
        />
      )}
      {cropModal}

        {/* Edit profile is handled as full-page takeover above */}

      <ConfirmDialog
        isOpen={showDiscardEditConfirm}
        onClose={() => setShowDiscardEditConfirm(false)}
        onConfirm={confirmDiscardEditDrawer}
        title="Forkaste endringer?"
        message="Du har ulagrede endringer i Rediger profil. Vil du forkaste disse?"
        confirmText="Forkast"
        cancelText="Fortsett redigering"
        variant="warning"
      />

        {/* Delete Post Confirmation Dialog */}
      <ConfirmDialog
        isOpen={showDeleteConfirm}
        onClose={() => {
          setShowDeleteConfirm(false);
          setPostToDelete(null);
        }}
        onConfirm={confirmDeletePost}
        title="Slett prosjekt?"
        message="Er du sikker på at du vil slette dette prosjektet? Denne handlingen kan ikke angres."
        confirmText="Slett"
        cancelText="Avbryt"
        variant="danger"
        loading={deletingPost}
        loadingText="Sletter..."
      />

        {/* Delete Package (Service) Confirmation Dialog */}
      <ConfirmDialog
        isOpen={showDeletePackageConfirm}
        onClose={() => {
          setShowDeletePackageConfirm(false);
          setPackageToDelete(null);
        }}
        onConfirm={confirmDeletePackage}
        title="Slett tjeneste?"
        message="Er du sikker på at du vil slette denne tjenesten? Denne handlingen kan ikke angres."
        confirmText="Slett"
        cancelText="Avbryt"
        variant="danger"
        loading={deletingPackage}
        loadingText="Sletter..."
      />
      </PageShell>
    </div>
    {activePackageInterests && isDesktop && accessToken && createPortal(
      <DesktopSubPageModal
        isOpen
        onBack={() => setActivePackageInterests(null)}
      >
        <TalentPackageInterestsView
          packageId={activePackageInterests.id}
          packageTitle={activePackageInterests.title}
          accessToken={accessToken}
          onBack={() => setActivePackageInterests(null)}
          onViewProfile={() => {}}
          onStartConversation={(targetUserId) => {
            setActivePackageInterests(null);
            onStartMessage?.(targetUserId);
          }}
          hideHeader
        />
      </DesktopSubPageModal>,
      document.body
    )}
    </>
  );
}
